/**
 * Driver Compliance Sub-section
 * Advanced driver safety and compliance insights with predictive analytics
 */

import { useState, useMemo, useEffect, useRef, useCallback, Fragment } from 'react';
import { Users, RefreshCw, AlertTriangle, CheckCircle, FileWarning, Shield, Activity, Search, ChevronDown, ChevronUp, ChevronRight, X, Clock } from 'lucide-react';
import { AquariusData, ApiStatus, DriverDetail } from '../../types/fleetDataHub';
import DonutChart from '../DonutChart';
import type { TimePeriod } from '../TimePeriodSelector';
import ControlCentreKPICard from './ControlCentreKPICard';
import ControlCentrePeriodPanel, {
  coerceControlCentrePeriod,
  DRIVER_CHECK_PERIOD_OPTIONS,
  DRIVER_HOURS_PERIOD_OPTIONS,
} from './ControlCentrePeriodPanel';
import { formatDataCurrentAs } from '../../utils/ordersDailyPresentation';
import KPIDataModal, { ColumnDef } from './KPIDataModal';
import ControlCentreSourceBadge from './ControlCentreSourceBadge';
import ControlCentreDailyChecksPdfExport from './ControlCentreDailyChecksPdfExport';
import DailySafetyBriefPanel, { type DailySafetyBriefNavigate } from './DailySafetyBriefPanel';
import DataTable from '../DataTable';
import Skeleton from '../ui/Skeleton';
import { Badge, type BadgeTone } from '../ui/badge';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import { API_ENDPOINTS } from '../../config/api';
import { authenticatedFetch } from '../../utils/apiClient';
import { useDriverViolationReviews, ViolationReviewControl, type DriverViolationReviews } from './DriverViolationReview';
import { CHART_PALETTE, STATUS_COLORS } from '../../config/colors';
import { driverComplianceSubtitle } from '../../utils/ingestSourceLabels';
import { formatDriverDisplayName } from '../../utils/formatDriverDisplayName';
import { londonDateString } from '../../utils/ordersDailyPresentation';
import { checkedCountOf, formatWalkaroundTimeHm, isCheckedStatus, shiftStartOf, type WalkaroundCheckStatus } from '../../utils/walkaroundCheckRows';
import { applyViolationsToStatus } from '../../utils/driverComplianceRules';
import { formatControlCentreDateTime } from '../../utils/formatControlCentreDateTime';
import { formatSuppliedClock, formatUkNumericDateTime } from '../../utils/ukTime';
import { controlCentrePeriodRange, type ControlCentrePeriod } from '../../utils/controlCentrePeriodRange';
import {
  STORED_VIEW_PREPARING_MESSAGE,
  STORED_VIEW_STALE_RECHECK_SECONDS,
  preparingRetrySeconds,
  storedViewStaleMessage,
} from '../../utils/storedView';
import {
  driverEntityIdsForAssignmentLookup,
  mapDriverOnVehicleEdgesToAssignments,
  remapVehiclesDrivenClamp,
  vehiclesDrivenCacheKey,
  vehiclesDrivenRowsToEdgeLikes,
  type DriverOnVehicleEdgeLike,
  type DriverVehicleAssignmentRow,
  type VehiclesDrivenCacheEntry,
} from '../../utils/driverVehicleAssignments';
import { defectStatusRowAccent, formatDefectStatusLabel } from '../../utils/defectStatus';

/** A driver row in the KPI detail modal: the API detail plus a derived violation count. */
type DriverModalRow = DriverDetail & { violationCount: number };

/** Row shape handed to DataTable for the driver walkaround-check table. */
type WalkaroundTableRow = {
  id: string;
  _check?: CheckComplianceRow;
  [key: string]: unknown;
};

const isAtRiskDriver = (d: DriverDetail) =>
  d.compliance_status === 'at-risk' || d.compliance_status === 'non-compliant';

type CheckComplianceStatus = WalkaroundCheckStatus;
type CheckTableFilter = 'all' | 'missing' | 'short' | 'defects';

/** Why hours were not evaluated, shown as the status label instead of "Unknown". */
function notEvaluatedLabel(driver: Pick<DriverDetail, 'no_tacho_data' | 'awaiting_next_shift'>): string {
  // The API's flags, so the Daily Driver Brief's "no tachograph data" count matches this label.
  if (driver.no_tacho_data) return 'No tacho data';
  if (driver.awaiting_next_shift) return 'Waiting for next shift';
  return 'Not evaluated';
}

/** Rules that were skipped for lack of facts (awaiting_next_shift has its own note). */
function skippedCheckGaps(driver: Pick<DriverDetail, 'evaluation_gaps'>): string[] {
  return (driver.evaluation_gaps ?? []).filter((g) => g !== 'awaiting_next_shift');
}

/** 0 evaluated, 1 Not evaluated, 2 No tacho data: unevaluated drivers sort after the rest. */
function unevaluatedSortRank(driver: Pick<DriverDetail, 'compliance_status' | 'no_tacho_data'>): number {
  if (driver.compliance_status !== 'unknown') return 0;
  return driver.no_tacho_data ? 2 : 1;
}

/** Driver Performance Details table status / hours filters. */
type DriverTableFilter = 'all' | 'compliant' | 'non-compliant' | 'no-tacho' | 'not-evaluated' | 'unknown-hours';

function rowMatchesDriverTableFilter(driver: DriverDetail, filter: DriverTableFilter): boolean {
  if (filter === 'all') return true;
  if (filter === 'compliant') return driver.compliance_status === 'compliant';
  if (filter === 'non-compliant') {
    // At Risk + Non-Compliant only. Unknown is not evaluated, not a breach.
    return driver.compliance_status === 'non-compliant'
      || driver.compliance_status === 'at-risk';
  }
  // Split as the row labels are (notEvaluatedLabel): no tacho data at all, or
  // tacho data the hours rules could not fully check (e.g. an incomplete week).
  if (filter === 'no-tacho') return driver.compliance_status === 'unknown' && Boolean(driver.no_tacho_data);
  if (filter === 'not-evaluated') return driver.compliance_status === 'unknown' && !driver.no_tacho_data;
  if (filter === 'unknown-hours') {
    return driver.unknown_hours != null && Number(driver.unknown_hours) > 0;
  }
  return true;
}

interface CheckComplianceRow {
  day?: string;
  driver_key: string;
  driver_name: string;
  vehicle: string;
  /** When the shift began (clocking on) — not the first drive. */
  shift_start_at?: string | null;
  /** Legacy name for shift_start_at; see utils/walkaroundCheckRows. */
  first_drive_at?: string | null;
  check_started_at: string | null;
  check_ended_at: string | null;
  /** Real instants of the three times above (backend applies the Aquarius mapping). */
  shift_start_instant?: string | null;
  check_started_instant?: string | null;
  check_ended_instant?: string | null;
  check_duration_minutes?: number | null;
  short_check?: boolean;
  check_result: string | null;
  defect_count: number;
  status: CheckComplianceStatus;
  /** Defects reported on the vehicle during this check (present when the check found defects). */
  defects?: WalkaroundDefect[];
}

/** A defect from Aquarius defects-by-date-range, matched to a check by vehicle and time. */
interface WalkaroundDefect {
  portal_id: string;
  title: string;
  note: string | null;
  reported_at: string;
  /** Real instant of `reported_at`. */
  reported_instant?: string | null;
  fixed: boolean;
  vor: boolean;
  status: string | null;
  also_on_another_check: boolean;
}

/** Not Fixed red, In Progress amber, Fixed green (as on the Maintenance tab). */
const DEFECT_STATUS_BADGE: Record<'red' | 'amber' | 'green' | 'neutral', string> = {
  red: 'bg-destructive/40 text-destructive border-destructive/60',
  amber: 'bg-warning/40 text-warning border-warning/60',
  green: 'bg-success/40 text-success border-success/60',
  neutral: 'bg-card/60 text-foreground border-accent/60',
};

/** What the driver reported during one check: shown under the walkaround table. */
function WalkaroundDefectDetails({ row }: { row: CheckComplianceRow }) {
  const defects = row.defects ?? [];
  const count = Number(row.defect_count ?? 0);
  return (
    <div className="rounded-lg border border-chart-5/60 bg-chart-5/10 p-3">
      {defects.length === 0 ? (
        <p className="text-sm text-foreground">
          The check recorded {count} defect{count === 1 ? '' : 's'}, but the details have not arrived from Aquarius yet.
        </p>
      ) : (
        <ul className="divide-y divide-chart-5/40">
          {defects.map((d) => (
            <li key={d.portal_id} className="py-2 first:pt-0 last:pb-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium text-foreground">{d.title || 'Defect'}</span>
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${
                    DEFECT_STATUS_BADGE[defectStatusRowAccent(d.fixed ? 'Fixed' : d.status, d.fixed) ?? 'neutral']
                  }`}
                >
                  {formatDefectStatusLabel(d.fixed ? 'Fixed' : d.status, d.fixed)}
                </span>
                {d.vor && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border bg-destructive/40 text-destructive border-destructive/60">
                    VOR
                  </span>
                )}
                <span className="text-xs text-muted-foreground">
                  {d.portal_id} · reported {formatTimeHm(d.reported_instant, d.reported_at)}
                </span>
              </div>
              {d.note && <p className="mt-1 text-sm text-foreground">{d.note}</p>}
              {d.also_on_another_check && (
                <p className="mt-1 text-xs text-muted-foreground">
                  Reported while another check on this vehicle was also open, so it is shown on both.
                </p>
              )}
            </li>
          ))}
          {defects.length !== count && (
            <li className="pt-2 text-xs text-muted-foreground">
              The check recorded {count} defect{count === 1 ? '' : 's'}; {defects.length} found in Aquarius defect records.
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

/**
 * The Driver tab's layout while a period loads (walkaround cards and table,
 * KPI cards, the two charts, the driver table), so switching period keeps the
 * page in place instead of collapsing to a spinner. Placeholders only: no
 * figure from another period is shown.
 */
/** Walkaround checks section while its period loads. */
function ChecksSectionSkeleton() {
  const card = 'rounded-lg border border-border bg-card p-4';
  return (
    <div className="space-y-3" aria-hidden>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className={card}>
            <Skeleton className="h-4 w-24 mb-3" />
            <Skeleton className="h-7 w-14" />
          </div>
        ))}
      </div>
      <div className={card}>
        <div className="space-y-2">
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-8 w-full" />)}
        </div>
      </div>
    </div>
  );
}

/** Drivers' hours section while its period loads. */
function HoursSectionSkeleton() {
  const card = 'rounded-lg border border-border bg-card p-4';
  return (
    <div className="space-y-4" aria-hidden>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {[0, 1].map((i) => (
          <div key={i} className={card}>
            <Skeleton className="h-4 w-28 mb-3" />
            <Skeleton className="h-8 w-20" />
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {[0, 1].map((i) => (
          <div key={i} className={card}>
            <Skeleton className="h-40 w-full" />
          </div>
        ))}
      </div>
    </div>
  );
}

function DriverTabSkeleton() {
  const card = 'rounded-lg border border-border bg-card p-4';
  return (
    <div className="space-y-6" aria-hidden>
      <div className="space-y-3">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className={card}>
              <Skeleton className="h-4 w-24 mb-3" />
              <Skeleton className="h-7 w-14" />
            </div>
          ))}
        </div>
        <div className={card}>
          <Skeleton className="h-5 w-64 mb-4" />
          <div className="space-y-2">
            {[0, 1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-8 w-full" />
            ))}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {[0, 1].map((i) => (
          <div key={i} className={card}>
            <Skeleton className="h-4 w-28 mb-3" />
            <Skeleton className="h-8 w-20 mb-2" />
            <Skeleton className="h-3 w-48" />
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {[0, 1].map((i) => (
          <div key={i} className={card}>
            <Skeleton className="h-5 w-40 mb-4" />
            <div className="flex items-center gap-6">
              <Skeleton className="h-40 w-40 rounded-full" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-5/6" />
                <Skeleton className="h-3 w-4/6" />
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className={card}>
        <Skeleton className="h-6 w-56 mb-4" />
        <div className="space-y-2">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      </div>
    </div>
  );
}

function formatCheckDuration(minutes: number | null | undefined): string {
  if (minutes == null || Number.isNaN(minutes)) return '—';
  if (minutes === 1) return '1 min';
  return `${minutes} min`;
}

const isPerformedCheck = isCheckedStatus;

function rowMatchesCheckFilter(row: CheckComplianceRow, filter: CheckTableFilter): boolean {
  if (filter === 'all') return true;
  if (filter === 'missing') return row.status === 'missing';
  if (filter === 'short') return Boolean(row.short_check);
  if (filter === 'defects') return Number(row.defect_count ?? 0) > 0;
  return true;
}

const formatTimeHm = formatWalkaroundTimeHm;

/**
 * Last Activity in UK time from the backend's real instant. Without one (older
 * response, or the window-end fallback when there is no shift row) the stored
 * value is shown as written.
 */
function formatLastActivity(value: unknown, instant: string | undefined): string {
  if (instant) return formatUkNumericDateTime(instant, instant);
  if (!value) return '—';
  const raw = String(value);
  return formatSuppliedClock(raw, { numeric: true }, raw);
}

function toDateLabel(iso: string | null | undefined): string {
  if (!iso) return '';
  const m = String(iso).match(/^(\d{4}-\d{2}-\d{2})/);
  if (m) return m[1];
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
}

/** Activity day for the walkaround table: "DD/MM" (year omitted — period already implies it). */
function toWalkaroundDayLabel(iso: string | null | undefined): string {
  const ymd = toDateLabel(iso);
  const m = ymd.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return ymd || '—';
  return `${m[3]}/${m[2]}`;
}

interface CheckComplianceResponse {
  kpis: {
    drivers_who_drove: number;
    missing: number;
    no_drive: number;
    /** Clock-ons not confirmed as drivers: not in a driver group (or no Samsara card link), no recent walkaround checks (not counted). */
    unverified?: number;
    /** True when drivers were verified against Samsara's driver list by tachograph card. */
    card_link_applied?: boolean;
    /** Employee groups declared as drivers, when that rule decided who counts; null otherwise. */
    driver_groups?: string[] | null;
    samsara_driver_cards?: number;
    /** Legacy (before the shift start was recognised as not the first drive); summed only as a fallback. */
    before_drive?: number;
    after_drive?: number;
    /** Server-computed checks recorded (preferred over the legacy sum). */
    checked?: number;
    /** Server-computed short-check count (preferred over filtering rows). */
    short_check?: number;
    /** Server-computed % check (preferred over client recalculation). */
    pct_checked?: number;
  };
  rows: CheckComplianceRow[];
}

/** Tachograph UNKNOWN-state time (not drive, work or break) — shown when present. */
const UNKNOWN_HOURS_COLUMN: ColumnDef<DriverDetail> = {
  key: 'unknown_hours',
  header: 'Unknown Hrs',
  align: 'right',
  render: (v) => (v != null && Number(v) > 0)
    ? <span className="text-chart-5 font-medium" title="Tachograph UNKNOWN state time: not drive, work or break">{Number(v).toFixed(1)}h</span>
    : '-',
};

/** "non-compliant" -> "Non-Compliant", "at-risk" -> "At Risk": the status as a title. */
function complianceStatusLabel(status: string): string {
  if (status === 'at-risk') return 'At Risk';
  return status.split('-').map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w)).join('-');
}

/** What a driver's violations were: the expanded row in the Driver Violations list. */
function DriverViolationList({ row, reviews }: { row: DriverDetail; reviews: DriverViolationReviews }) {
  const violations = [...(row.violations ?? [])].sort((a, b) => String(a.date).localeCompare(String(b.date)));
  if (violations.length === 0) return null;
  const sev: Record<string, string> = {
    critical: 'bg-destructive/40 text-destructive border-destructive/60',
    warning: 'bg-warning/40 text-warning border-warning/60',
    minor: 'bg-secondary/60 text-foreground border-accent',
  };
  return (
    <ul className="divide-y divide-border/60 rounded-lg border border-destructive/50 bg-destructive/10 px-3">
      {reviews.error ? (
        <li className="py-2 text-xs text-warning">Couldn't load who resolved these violations ({reviews.error}).</li>
      ) : null}
      {violations.map((v, i) => (
        <li key={v.id ?? `${v.date}-${v.type}-${i}`} className="py-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-foreground">{v.label || v.type}</span>
            {/* A severity outside the three known ones gets no badge, never "Minor". */}
            {sev[v.severity] ? (
              <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium border ${sev[v.severity]}`}>
                {v.severity === 'critical' ? 'Critical' : v.severity === 'warning' ? 'Warning' : 'Minor'}
              </span>
            ) : null}
            <span className="text-xs text-muted-foreground">{formatViolationDay(v.date)}</span>
          </div>
          {v.description ? <p className="mt-1 text-sm text-foreground">{v.description}</p> : null}
          <ViolationReviewControl driverId={String(row.driver_id)} violation={v} state={reviews} />
        </li>
      ))}
    </ul>
  );
}

function formatViolationDay(date: string): string {
  const d = new Date(`${String(date).slice(0, 10)}T12:00:00Z`);
  if (Number.isNaN(d.getTime())) return String(date);
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
}

/** Window for a Driver tab chip; the chips are zeecore's stored Driver tab periods, "all time" is read live. */
function driverPeriodRange(selected: TimePeriod): { startDate: Date; endDate: Date } {
  if (selected === 'all-time') {
    return { startDate: new Date('2000-01-01T00:00:00.000Z'), endDate: new Date('2099-12-31T23:59:59.999Z') };
  }
  const shared: ControlCentrePeriod[] = [
    'today', 'yesterday', 'last-week', 'last-full-week', 'month-to-date', 'last-month', 'last_30_days', 'last_180_days',
  ];
  // Chip bars have no custom picker: treat unknown/custom like Yesterday.
  const period = shared.includes(selected as ControlCentrePeriod) ? selected as ControlCentrePeriod : 'yesterday';
  const { start, end } = controlCentrePeriodRange(period);
  return { startDate: start, endDate: end };
}

/** The Driver Violations list leads with the drivers who broke the most rules. */
const MOST_VIOLATIONS_FIRST = { key: 'violationCount', direction: 'desc' } as const;

// Column definitions for driver KPI detail modal
const DRIVER_COLUMNS: ColumnDef<DriverDetail>[] = [
  { key: 'driver_name', header: 'Driver' },
  { key: 'compliance_status', header: 'Status', render: (v, row) => {
    const s = String(v || '').toLowerCase();
    const tone: BadgeTone = s === 'compliant' ? 'success'
      : s === 'non-compliant' ? 'destructive'
      : s === 'unknown' ? 'neutral'
      : 'warning';
    const label = s === 'unknown' || !s ? notEvaluatedLabel(row ?? {}) : complianceStatusLabel(s);
    return <Badge tone={tone} className="py-1 whitespace-nowrap">{label}</Badge>;
  }},
  { key: 'shift_hours', header: 'Shift Hrs', align: 'right', render: (v) => (v != null && Number(v) > 0) ? `${Number(v).toFixed(1)}h` : '-' },
  { key: 'driving_hours', header: 'Driving Hrs', align: 'right', render: (v) => v ? `${Number(v).toFixed(1)}h` : '-' },
  { key: 'rest_hours', header: 'Break Hrs', align: 'right', render: (v) => v ? `${Number(v).toFixed(1)}h` : '-' },
  { key: 'violationCount', header: 'Violations', align: 'right', render: (v) => {
    const count = Number(v) || 0;
    return count > 0 ? <span className="text-destructive font-bold">{count}</span> : <span className="text-success">0</span>;
  }},
  { key: 'last_activity', header: 'Last Activity', render: (v, row) =>
    formatLastActivity(v, (row as DriverDetail).last_activity_instant) },
];

interface DriverComplianceProps {
  data: AquariusData | null;
  apiStatus: ApiStatus | undefined;
  onFetchData: () => void | Promise<void>;
  isLoading: boolean;
  highlightKPI?: string | null;
  /** Clear parent highlight after alert deep-link is consumed (avoids modal reopen loops) */
  onHighlightConsumed?: () => void;
  /** When set (e.g. from Fleet Alerts), sync the section time period to this value */
  initialPeriod?: TimePeriod;
  /** When true, use prop data only (no period-scoped live refetch) */
  useMockData?: boolean;
  /** Org-configured driver source display name, e.g. "Aquarius" */
  driverSourceLabel?: string;
  /** Org-configured driver source ids for subtitle helpers */
  driverSourceIds?: string[];
  /** Navigate to Maintenance tab (optionally with a KPI highlight) */
  onNavigateToMaintenance?: (highlight?: string) => void;
  /**
   * Incremented when Control Centre / browser tab becomes visible again so the
   * period summary re-reads Iceberg without requiring Refresh All.
   */
  refreshKey?: number;
  /** Stamp parent visibility cooldown when a Driver Iceberg fetch starts. */
  onTruthFetchStart?: () => void;
}

export default function DriverCompliance({
  data,
  onFetchData,
  isLoading,
  highlightKPI,
  onHighlightConsumed,
  initialPeriod,
  useMockData = false,
  driverSourceLabel,
  driverSourceIds = [],
  onNavigateToMaintenance,
  refreshKey = 0,
  onTruthFetchStart,
}: DriverComplianceProps) {
  // Walkaround checks and defects (top of the tab).
  const [selectedPeriod, setSelectedPeriod] = useState<TimePeriod>(
    () => coerceControlCentrePeriod(initialPeriod, 'today', DRIVER_CHECK_PERIOD_OPTIONS),
  );
  // Drivers' hours and violations: tacho data is about a day behind, so no Today.
  const [hoursPeriod, setHoursPeriod] = useState<TimePeriod>(
    () => coerceControlCentrePeriod(initialPeriod, 'yesterday', DRIVER_HOURS_PERIOD_OPTIONS),
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedDriver, setExpandedDriver] = useState<{
    driverId: string;
    activityEntityId?: string;
  } | null>(null);
  const [vehicleAssignments, setVehicleAssignments] = useState<{
    driverId: string;
    loading: boolean;
    error: string | null;
    rows: DriverVehicleAssignmentRow[];
  } | null>(null);
  /** Period-scoped graph payloads — avoids re-scanning on collapse/re-expand. */
  const vehiclesDrivenCacheRef = useRef<Map<string, VehiclesDrivenCacheEntry>>(new Map());
  const [sortField, setSortField] = useState<'name' | 'status' | 'shift' | 'driving' | 'work' | 'rest' | 'violations'>('status');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [showViolationsModal, setShowViolationsModal] = useState(false);
  const [periodData, setPeriodData] = useState<AquariusData | null>(null);
  /** ISO from/to that `periodData` was fetched for — avoids Today rules on stale Last-7-Days rows. */
  const [periodDataRange, setPeriodDataRange] = useState<{ from: string; to: string } | null>(null);
  const periodDataRangeRef = useRef<{ from: string; to: string } | null>(null);
  const [periodLoading, setPeriodLoading] = useState(false);
  const [periodError, setPeriodError] = useState<string | null>(null);
  /** The selected preset period's stored figures are still being built (202); a retry is scheduled. */
  const [periodPreparing, setPeriodPreparing] = useState(false);
  /** Next automatic re-read: after "preparing", or after stale stored figures. */
  const periodRecheckTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [checkCompliance, setCheckCompliance] = useState<CheckComplianceResponse | null>(null);
  // Walkaround checks load on their own, for the checks period (see fetchChecks).
  const [checksLoading, setChecksLoading] = useState(false);
  const [checksError, setChecksError] = useState<string | null>(null);
  const [checksPreparing, setChecksPreparing] = useState(false);
  const [checksDataRange, setChecksDataRange] = useState<{ from: string; to: string } | null>(null);
  const checksDataRangeRef = useRef<{ from: string; to: string } | null>(null);
  const checksRecheckTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [checkStatusFilter, setCheckStatusFilter] = useState<CheckTableFilter>('all');
  const [driverTableFilter, setDriverTableFilter] = useState<DriverTableFilter>('all');
  const walkaroundTableRef = useRef<HTMLDivElement>(null);
  const driverTableFiltersRef = useRef<HTMLDivElement>(null);

  // KPI detail modal state
  const [kpiModal, setKpiModal] = useState<{ open: boolean; title: string; subtitle?: string; filterFn?: (row: DriverModalRow) => boolean; defaultSort?: { key: string; direction?: 'asc' | 'desc' } }>({ open: false, title: '' });
  const kpiGridRef = useRef<HTMLDivElement>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  type DriverSortField = 'name' | 'status' | 'shift' | 'driving' | 'work' | 'rest' | 'violations';

  const handleSortHeaderClick = (field: DriverSortField) => {
    if (sortField === field) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection(field === 'name' ? 'asc' : 'desc');
    }
    setCurrentPage(1);
  };

  const sortHeaderClass = (field: DriverSortField) =>
    `text-left py-3 px-4 text-sm font-semibold cursor-pointer select-none transition-colors ${
      sortField === field ? 'text-white' : 'text-foreground hover:text-white'
    }`;

  const renderSortIcon = (field: DriverSortField) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc'
      ? <ChevronUp className="w-4 h-4 shrink-0" />
      : <ChevronDown className="w-4 h-4 shrink-0" />;
  };

  // Sync time period only when an alert/deep-link explicitly provides a period
  useEffect(() => {
    if (initialPeriod != null && highlightKPI) {
      setSelectedPeriod(coerceControlCentrePeriod(initialPeriod, 'today', DRIVER_CHECK_PERIOD_OPTIONS));
      setHoursPeriod(coerceControlCentrePeriod(initialPeriod, 'yesterday', DRIVER_HOURS_PERIOD_OPTIONS));
    }
  }, [initialPeriod, highlightKPI]);

  const applyCheckStatusFilter = useCallback((status: CheckTableFilter) => {
    setCheckStatusFilter(status);
    requestAnimationFrame(() => {
      walkaroundTableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);

  const handleBriefNavigate = useCallback((target: DailySafetyBriefNavigate) => {
    if (target === 'checks_missing') {
      applyCheckStatusFilter('missing');
      return;
    }
    if (target === 'checks_unverified') {
      // No "Not verified" filter: those rows are in the full table, badged.
      applyCheckStatusFilter('all');
      return;
    }
    if (target === 'checks_short') {
      applyCheckStatusFilter('short');
      return;
    }
    if (target === 'checks_done' || target === 'checks_late' || target === 'checks_before') {
      // No "Checked" filter: completed checks are in the full table.
      applyCheckStatusFilter('all');
      return;
    }
    if (target === 'compliance') {
      setKpiModal({
        open: true,
        title: 'Driver Compliance',
        subtitle: 'Hours compliance across drivers in this window',
      });
      requestAnimationFrame(() => {
        kpiGridRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
      return;
    }
    if (target === 'violations') {
      setKpiModal({
        open: true,
        title: 'Driver Violations',
        subtitle: 'Drivers with driving-time infringements',
        filterFn: (d: DriverModalRow) => (d.violationCount || 0) > 0,
        defaultSort: MOST_VIOLATIONS_FIRST,
      });
      requestAnimationFrame(() => {
        kpiGridRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
      return;
    }
    if (target === 'maintenance_vor') {
      onNavigateToMaintenance?.('vor');
      return;
    }
    if (target === 'maintenance_faults') {
      onNavigateToMaintenance?.('active-faults');
      return;
    }
    onNavigateToMaintenance?.('open-defects');
  }, [applyCheckStatusFilter, onNavigateToMaintenance]);

  // Date ranges for the two chip bars: walkaround checks (getDateRange) and
  // drivers' hours (hoursRange). Both use zeecore's stored Driver tab windows.
  const getDateRange = useMemo(() => driverPeriodRange(selectedPeriod), [selectedPeriod]);
  const hoursRange = useMemo(() => driverPeriodRange(hoursPeriod), [hoursPeriod]);

  // Who resolved which violation, shared by the organisation (not per browser).
  const reviewRange = useMemo(
    () => (useMockData ? null : {
      // Full instants, as the route validates them like the Driver tab's other reads; it keeps the date.
      from: hoursRange.startDate.toISOString(),
      to: hoursRange.endDate.toISOString(),
    }),
    [useMockData, hoursRange],
  );
  const violationReviews = useDriverViolationReviews(reviewRange);

  const isSingleDayPeriod = hoursPeriod === 'today' || hoursPeriod === 'yesterday';

  // Must stay above early returns — Rules of Hooks.
  // Today is still open: zero hours usually means shifts not closed yet, not a blank fleet.
  const viewingLiveToday = useMemo(() => {
    if (hoursPeriod === 'today') return true;
    const todayStr = londonDateString();
    const from = londonDateString(hoursRange.startDate);
    const to = londonDateString(hoursRange.endDate);
    return from === todayStr && to === todayStr;
  }, [hoursPeriod, hoursRange]);

  // Identifies the most recent period fetch so a slower older response cannot
  // overwrite a newer refreshKey / period change.
  const periodRequestSeq = useRef(0);
  /** Successful period reads; the brief reloads when it changes (same triggers as this tab). */
  const [periodLoadCount, setPeriodLoadCount] = useState(0);

  /** `fresh`: a re-read (refreshKey / Refresh), so skip the server's response cache. */
  const fetchPeriodSummary = useCallback(async (fresh = false) => {
    if (useMockData) {
      setPeriodData(null);
      setPeriodDataRange(null);
      setPeriodError(null);
      return;
    }

    const seq = ++periodRequestSeq.current;
    if (periodRecheckTimer.current) clearTimeout(periodRecheckTimer.current);
    periodRecheckTimer.current = null;
    // Drivers' hours for the hours period; the checks load on their own (fetchChecks).
    const from = hoursRange.startDate.toISOString();
    const to = hoursRange.endDate.toISOString();
    onTruthFetchStart?.();
    setPeriodLoading(true);
    setPeriodError(null);
    try {
      const res = await authenticatedFetch(API_ENDPOINTS.fleetIntelligence.driversSummary({ from, to }, { fresh }));
      if (seq !== periodRequestSeq.current) return;
      // Preset periods are stored views: 202 = the first copy is being built.
      // Ask again when it should be ready; nothing is computed per request.
      if (res.status === 202) {
        const wait = await preparingRetrySeconds(res);
        if (seq !== periodRequestSeq.current) return;
        setPeriodPreparing(true);
        periodRecheckTimer.current = setTimeout(() => { void fetchPeriodSummaryRef.current(); }, wait * 1000);
        return;
      }
      setPeriodPreparing(false);
      if (!res.ok) {
        throw new Error(`Driver summary failed (${res.status})`);
      }
      const json = await res.json();
      if (seq !== periodRequestSeq.current) return;
      setPeriodData(json as AquariusData);
      setPeriodLoadCount((n) => n + 1);
      setPeriodDataRange({ from, to });
      periodDataRangeRef.current = { from, to };
      // Stale stored figures: shown with a note; look again once the requested refresh has landed.
      if ((json as AquariusData).projection?.stale_reason) {
        periodRecheckTimer.current = setTimeout(
          () => { void fetchPeriodSummaryRef.current(); },
          STORED_VIEW_STALE_RECHECK_SECONDS * 1000,
        );
      }
    } catch (err) {
      if (seq !== periodRequestSeq.current) return;
      console.error('[DriverCompliance] Period summary fetch failed:', err);
      setPeriodError(err instanceof Error ? err.message : 'Failed to load driver hours');
      // Never keep mismatched stale rows (e.g. Last 7 Days painted under Today rules).
      const matched =
        periodDataRangeRef.current?.from === from && periodDataRangeRef.current?.to === to;
      if (!matched) {
        setPeriodData(null);
        setPeriodDataRange(null);
        periodDataRangeRef.current = null;
      }
    } finally {
      if (seq === periodRequestSeq.current) setPeriodLoading(false);
    }
  }, [useMockData, hoursRange, onTruthFetchStart]);

  // Walkaround checks for the checks period, loaded on their own so changing
  // either period reloads only its own section.
  const checksRequestSeq = useRef(0);
  const fetchChecksRef = useRef<() => Promise<void>>(async () => {});
  const fetchChecks = useCallback(async () => {
    if (useMockData) {
      setCheckCompliance(null);
      setChecksError(null);
      return;
    }
    const seq = ++checksRequestSeq.current;
    if (checksRecheckTimer.current) clearTimeout(checksRecheckTimer.current);
    checksRecheckTimer.current = null;
    const from = getDateRange.startDate.toISOString();
    const to = getDateRange.endDate.toISOString();
    setChecksLoading(true);
    setChecksError(null);
    try {
      const res = await authenticatedFetch(API_ENDPOINTS.fleetIntelligence.driversCheckCompliance({ from, to }));
      if (seq !== checksRequestSeq.current) return;
      if (res.status === 202) {
        const wait = await preparingRetrySeconds(res);
        if (seq !== checksRequestSeq.current) return;
        setChecksPreparing(true);
        checksRecheckTimer.current = setTimeout(() => { void fetchChecksRef.current(); }, wait * 1000);
        return;
      }
      setChecksPreparing(false);
      if (!res.ok) throw new Error(`Walkaround checks failed (${res.status})`);
      const json = (await res.json()) as CheckComplianceResponse;
      if (seq !== checksRequestSeq.current) return;
      setCheckCompliance(json);
      setChecksDataRange({ from, to });
      checksDataRangeRef.current = { from, to };
    } catch (err) {
      if (seq !== checksRequestSeq.current) return;
      console.error('[DriverCompliance] Walkaround checks fetch failed:', err);
      setChecksError(err instanceof Error ? err.message : 'Failed to load walkaround checks');
      // Never show another period's checks under this chip.
      if (checksDataRangeRef.current?.from !== from || checksDataRangeRef.current?.to !== to) {
        setCheckCompliance(null);
        setChecksDataRange(null);
        checksDataRangeRef.current = null;
      }
    } finally {
      if (seq === checksRequestSeq.current) setChecksLoading(false);
    }
  }, [useMockData, getDateRange]);

  useEffect(() => {
    fetchChecksRef.current = fetchChecks;
  }, [fetchChecks]);

  useEffect(() => {
    void fetchChecks();
  }, [fetchChecks, refreshKey]);

  useEffect(() => () => {
    if (checksRecheckTimer.current) clearTimeout(checksRecheckTimer.current);
  }, []);

  useEffect(() => {
    // A refreshKey bump means newer data may have landed: a cached response
    // (up to 45 s old) could still carry the previous "Data current as of".
    void fetchPeriodSummary(refreshKey > 0);
  }, [fetchPeriodSummary, refreshKey]);

  useEffect(() => () => {
    if (periodRecheckTimer.current) clearTimeout(periodRecheckTimer.current);
  }, []);

  // Reset table filters + vehicles-driven cache when the date window changes.
  useEffect(() => {
    setCheckStatusFilter('all');
    setDriverTableFilter('all');
    setCurrentPage(1);
    vehiclesDrivenCacheRef.current.clear();
    setExpandedDriver(null);
    setVehicleAssignments(null);
  }, [periodDataRange?.from, periodDataRange?.to]);

  const applyDriverTableFilter = useCallback((next: DriverTableFilter) => {
    setDriverTableFilter(next);
    setCurrentPage(1);
  }, []);

  // Load DRIVER_ON_VEHICLE windows when a driver row is expanded (period-scoped).
  // Prefer vehicles_driven attached on drivers/summary over any client cache;
  // fall back to graph fetches (Aquarius + Samsara sibling). Successful results
  // are cached for the period — hard failures are not (so expand can retry).
  useEffect(() => {
    if (!expandedDriver || useMockData) {
      setVehicleAssignments(null);
      return;
    }

    const from =
      periodDataRange?.from
      || hoursRange.startDate.toISOString();
    const to =
      periodDataRange?.to
      || hoursRange.endDate.toISOString();
    // Open assignments: clamp duration to "now" when viewing live today, else period end.
    const clampToIso = viewingLiveToday
      ? new Date().toISOString()
      : to;

    const entityIds = driverEntityIdsForAssignmentLookup({
      driver_id: expandedDriver.driverId,
      activity_entity_id: expandedDriver.activityEntityId,
    });
    const cacheKey = vehiclesDrivenCacheKey({
      driverId: expandedDriver.driverId,
      activityEntityId: expandedDriver.activityEntityId,
      from,
      to,
    });

    const showRows = (
      rows: DriverVehicleAssignmentRow[],
      error: string | null = null,
    ) => {
      setVehicleAssignments({
        driverId: expandedDriver.driverId,
        loading: false,
        error,
        rows,
      });
    };

    // Summary attach wins over cache so a later summary refresh is not shadowed
    // by an earlier graph fallback written before vehicles_driven arrived.
    const summaryDriver = (periodData?.driver_details ?? []).find(
      (d) => d.driver_id === expandedDriver.driverId,
    );
    if (summaryDriver && Array.isArray(summaryDriver.vehicles_driven)) {
      const summaryRows: DriverVehicleAssignmentRow[] = summaryDriver.vehicles_driven.map((row) => ({
        vehicle_entity_id: row.vehicle_entity_id,
        vehicle_label: row.vehicle_label,
        start: row.start,
        end: row.end,
        open_ended: row.open_ended,
        duration_hours: row.duration_hours,
      }));
      const entityLabels = Object.fromEntries(
        summaryRows.map((r) => [r.vehicle_entity_id, r.vehicle_label]),
      );
      vehiclesDrivenCacheRef.current.set(cacheKey, {
        edges: vehiclesDrivenRowsToEdgeLikes(summaryRows, expandedDriver.driverId),
        entityLabels,
        entityIds,
        error: null,
      });
      // Summary durations used period end — remap open windows for live Today.
      showRows(remapVehiclesDrivenClamp(summaryRows, clampToIso));
      return;
    }

    const cached = vehiclesDrivenCacheRef.current.get(cacheKey);
    if (cached) {
      // Already mapped with clampToIso — do not remap again.
      showRows(
        mapDriverOnVehicleEdgesToAssignments(cached.edges, {
          driverEntityId: expandedDriver.driverId,
          siblingEntityIds: cached.entityIds,
          entityLabels: cached.entityLabels,
          clampToIso,
        }),
        cached.error,
      );
      return;
    }

    let cancelled = false;
    setVehicleAssignments({
      driverId: expandedDriver.driverId,
      loading: true,
      error: null,
      rows: [],
    });

    void (async () => {
      type GraphPayload = {
        edges?: DriverOnVehicleEdgeLike[];
        entityLabels?: Record<string, string>;
      };

      const settled = await Promise.allSettled(
        entityIds.map(async (entityId) => {
          const url = API_ENDPOINTS.truthBrowser.graphRelationships('driver', entityId, {
            from,
            to,
            relationshipType: 'DRIVER_ON_VEHICLE',
          });
          const res = await authenticatedFetch(url);
          if (!res.ok) {
            throw new Error(`Vehicle assignments failed (${res.status})`);
          }
          return res.json() as Promise<GraphPayload>;
        }),
      );
      if (cancelled) return;

      const responses = settled
        .filter((r): r is PromiseFulfilledResult<GraphPayload> => r.status === 'fulfilled')
        .map((r) => r.value);
      const failures = settled.filter((r) => r.status === 'rejected');

      // Only hard-fail when every sibling lookup failed — otherwise show edges
      // from the successful ids (Aquarius vs Samsara may differ in availability).
      if (responses.length === 0) {
        const firstErr = failures[0] && failures[0].status === 'rejected'
          ? failures[0].reason
          : null;
        const error = firstErr instanceof Error
          ? firstErr.message
          : 'Failed to load vehicle assignments';
        // Do not cache hard failures — re-expand must be able to retry.
        showRows([], error);
        return;
      }

      const edges = responses.flatMap((json) => json.edges ?? []);
      const entityLabels = Object.assign(
        {},
        ...responses.map((json) => json.entityLabels ?? {}),
      ) as Record<string, string>;
      const softError = failures.length > 0
        ? 'Some vehicle lookups failed; showing available assignments'
        : null;

      vehiclesDrivenCacheRef.current.set(cacheKey, {
        edges,
        entityLabels,
        entityIds,
        error: softError,
      });

      showRows(
        mapDriverOnVehicleEdgesToAssignments(edges, {
          driverEntityId: expandedDriver.driverId,
          siblingEntityIds: entityIds,
          entityLabels,
          clampToIso,
        }),
        softError,
      );
    })();

    return () => {
      cancelled = true;
    };
  }, [
    expandedDriver,
    useMockData,
    periodData,
    periodDataRange?.from,
    periodDataRange?.to,
    hoursRange,
    viewingLiveToday,
  ]);

  // fetchPeriodSummary changes identity on every period/date change, and the
  // effect below must NOT re-fire for that reason — the effect above already
  // handles period changes. Route through a ref so the effect can call the
  // latest fetchPeriodSummary without depending on its identity.
  const fetchPeriodSummaryRef = useRef(fetchPeriodSummary);
  useEffect(() => {
    fetchPeriodSummaryRef.current = fetchPeriodSummary;
  }, [fetchPeriodSummary]);

  // After Control Centre Refresh All replaces hub aquariusData, re-read the
  // period-scoped Iceberg summary so the Driver tab does not stay on stale
  // rows. Deps are deliberately just [data, useMockData] — this must fire
  // only when the hub data prop itself changes, not on every period change
  // (fetchPeriodSummary was previously in the deps here too, so a period
  // change fired this effect a second time on top of the one above).
  const skipHubDataSyncRef = useRef(true);
  useEffect(() => {
    if (useMockData || !data) return;
    if (skipHubDataSyncRef.current) {
      skipHubDataSyncRef.current = false;
      return;
    }
    void fetchPeriodSummaryRef.current();
    void fetchChecksRef.current();
  }, [data, useMockData]);

  /** Load / refresh = Iceberg drivers/summary only — never legacy Aquarius export. */
  //
  // Live mode re-reads only this tab's period summary + walkaround checks. It
  // used to reload the whole Control Centre hub first (fleet, fuel, live
  // vehicles, orders and another drivers/summary): the hub data change then
  // re-fired the period fetch below, so one click computed drivers/summary
  // three times, all sharing the server's small Athena slot pool. Other tabs
  // refresh from the Control Centre header.
  const handleRefresh = useCallback(async () => {
    if (useMockData) {
      await Promise.resolve(onFetchData());
      return;
    }
    await fetchPeriodSummary(true);
  }, [useMockData, onFetchData, fetchPeriodSummary]);

  // Walkaround checks period (PDF export, brief) and the hours period the summary was read for.
  const selectedFrom = getDateRange.startDate.toISOString();
  const selectedTo = getDateRange.endDate.toISOString();
  const periodDataMatchesSelection =
    !!periodData
    && periodDataRange?.from === hoursRange.startDate.toISOString()
    && periodDataRange?.to === hoursRange.endDate.toISOString();

  // Live mode: only show hours for the selected window. Never fall back to parent
  // aquariusData (often a 180-day Control Centre load) — that made Today show 30h+ totals.
  const sourceData = useMockData ? data : (periodDataMatchesSelection ? periodData : null);

  // Derive display status and totals. A resolved violation still counts: resolving records a review, it doesn't undo the violation.
  const filteredData = useMemo(() => {
    if (!sourceData) return null;

    const filtered = { ...sourceData };

    if (filtered.driver_details && filtered.driver_details.length > 0) {
      filtered.driver_details = filtered.driver_details.map(driver => {
        const activeViolations = (driver.violations || [])
          .map((violation, idx) => ({
            ...violation,
            id: violation.id || `${driver.driver_id}-${violation.type}-${violation.date}-${idx}`,
          }));

        const drivingHours = Number(driver.driving_hours || 0);
        const restHours = Number(driver.rest_hours || 0);
        const shiftHours = Number(driver.shift_hours || 0);
        const hoursSource =
          driver.hours_source
          || ((drivingHours > 0 || restHours > 0)
            ? 'activity'
            : (shiftHours > 0 ? 'ta_shift' : 'activity'));
        // T&A clocked duration has no drive/rest split — do not run HOS rest checks on it.
        const hasActivityDetail = hoursSource === 'activity' || hoursSource === 'mixed';

        // Trust server compliance / risk from drivers/summary. Do not invent
        // At Risk from local 8h-rest / 10h-drive thresholds — those compared
        // in-day BREAK/REST totals to daily-rest limits and false-flagged fleets.
        const apiStatus = driver.compliance_status;
        let complianceStatus: 'compliant' | 'non-compliant' | 'at-risk' | 'unknown' =
          apiStatus === 'compliant'
          || apiStatus === 'non-compliant'
          || apiStatus === 'at-risk'
          || apiStatus === 'unknown'
            ? apiStatus
            : 'unknown';
        // Risk: keep null when API omitted / unknown — never coerce missing to 0 (green).
        let riskScore: number | null =
          typeof driver.risk_score === 'number' && Number.isFinite(driver.risk_score)
            ? driver.risk_score
            : null;

        if (activeViolations.length > 0) {
          const next = applyViolationsToStatus(complianceStatus, riskScore, activeViolations);
          complianceStatus = next.status;
          riskScore = next.riskScore;
        } else if (
          !isSingleDayPeriod
          && driver.hours_compliance === true
          && complianceStatus !== 'unknown'
        ) {
          // Multi-day window with no daily driving-time violations: don't flag At Risk
          // just because period totals exceed a single-day limit. Keep API risk as-is.
          complianceStatus = 'compliant';
        }

        if (riskScore != null) {
          riskScore = Math.max(0, Math.min(100, Math.round(riskScore)));
        }

        const hoursCompliance =
          hasActivityDetail
            ? (typeof driver.hours_compliance === 'boolean' ? driver.hours_compliance : null)
            : null;
        const restCompliance =
          hasActivityDetail
            ? (typeof driver.rest_compliance === 'boolean' ? driver.rest_compliance : null)
            : null;

        return {
          ...driver,
          driver_name: formatDriverDisplayName(driver.driver_name),
          violations: activeViolations,
          compliance_status: complianceStatus,
          risk_score: riskScore,
          hours_source: hoursSource,
          shift_hours: shiftHours,
          hours_compliance: hoursCompliance,
          rest_compliance: restCompliance,
          driving_hours: drivingHours,
          rest_hours: restHours,
        };
      });

      filtered.driver_count = filtered.driver_details.length;
      filtered.infringement_count = filtered.driver_details.reduce((sum, d) => sum + (d.violations?.length || 0), 0);
      filtered.total_driving_hours = filtered.driver_details.reduce((sum, d) => sum + (d.driving_hours || 0), 0);
      filtered.total_rest_hours = filtered.driver_details.reduce((sum, d) => sum + (d.rest_hours || 0), 0);
      filtered.total_work_hours = filtered.driver_details.reduce((sum, d) => sum + (d.work_hours || 0), 0);
      filtered.total_unknown_hours = filtered.driver_details.reduce((sum, d) => sum + (d.unknown_hours || 0), 0);
      filtered.total_driving_plus_work_hours =
        (filtered.total_driving_hours || 0) + (filtered.total_work_hours || 0);
      filtered.total_shift_hours = filtered.driver_details.reduce((sum, d) => sum + (d.shift_hours || 0), 0);
      filtered.total_available_hours = 0;
      filtered.total_other_hours = 0;

      const compliantDrivers = filtered.driver_details.filter(d => {
        const hasActiveViolations = (d.violations?.length || 0) > 0;
        return !hasActiveViolations
          && d.compliance_status === 'compliant';
      }).length;

      const notEvaluatedDrivers = filtered.driver_details.filter(
        (d) => d.compliance_status === 'unknown',
      ).length;
      const evaluatedDrivers = filtered.driver_count - notEvaluatedDrivers;

      filtered.driver_hours_compliant = compliantDrivers;
      filtered.driver_hours_not_evaluated = notEvaluatedDrivers;
      // Unknown (not evaluated) is neither compliant nor non-compliant.
      filtered.compliance_percentage = evaluatedDrivers > 0
        ? Math.round((compliantDrivers / evaluatedDrivers) * 100)
        : 0;
    }

    return filtered;
  }, [sourceData, isSingleDayPeriod]);

  // Keep hook ordering stable even when data is null
  const displayData = filteredData || sourceData;
  const totalDrivers = displayData?.driver_count || 0;
  const awaitingNextShiftCount = displayData?.driver_details?.filter((d) => d.awaiting_next_shift).length ?? 0;
  // While the selected window has no matching response yet, keep the loading state so we
  // don't flash the empty "Load Driver Data" prompt or apply Today rules to stale rows.
  const showLoading =
    isLoading
    || periodLoading
    || (!useMockData && !periodDataMatchesSelection && !periodError);
  // The checks on screen are for the selected checks period (never another period's).
  const checksMatchSelection =
    !!checkCompliance
    && checksDataRange?.from === selectedFrom
    && checksDataRange?.to === selectedTo;
  const shownChecks = useMockData ? checkCompliance : (checksMatchSelection ? checkCompliance : null);

  // Build driver rows for KPI detail modal
  const driverRows = useMemo(() => {
    const details = displayData?.driver_details || [];
    return details.map(d => ({
      ...d,
      violationCount: d.violations?.length || 0,
    }));
  }, [displayData]);

  // Unknown hours appear in the modal only when a driver in it has some.
  const kpiModalColumns = useMemo(() => {
    const rows = kpiModal.filterFn ? driverRows.filter(kpiModal.filterFn) : driverRows;
    if (!rows.some((r) => Number(r.unknown_hours) > 0)) return DRIVER_COLUMNS;
    const at = DRIVER_COLUMNS.findIndex((c) => c.key === 'rest_hours') + 1;
    return [...DRIVER_COLUMNS.slice(0, at), UNKNOWN_HOURS_COLUMN, ...DRIVER_COLUMNS.slice(at)];
  }, [driverRows, kpiModal.filterFn]);

  const pctCheck = useMemo(() => {
    if (!shownChecks) return 0;
    const { drivers_who_drove, pct_checked } = shownChecks.kpis;
    if (typeof pct_checked === 'number') return pct_checked;
    if (drivers_who_drove <= 0) return 0;
    return Math.round((checkedCountOf(shownChecks.kpis) / drivers_who_drove) * 1000) / 10;
  }, [shownChecks]);

  /** Checks (driver-days) that recorded at least one defect. */
  const defectsFoundCount = useMemo(
    () => (shownChecks?.rows || []).filter((r) => Number(r.defect_count ?? 0) > 0).length,
    [shownChecks],
  );

  const shortCheckCount = useMemo(() => {
    if (!shownChecks) return 0;
    if (typeof shownChecks.kpis.short_check === 'number') return shownChecks.kpis.short_check;
    return (shownChecks.rows || []).filter((r) => r.short_check).length;
  }, [shownChecks]);

  /** Walkaround completion: red <70%, amber 70–89%, green ≥90% */
  const walkaroundPctTone = useMemo(() => {
    if (pctCheck >= 90) return 'green' as const;
    if (pctCheck >= 70) return 'amber' as const;
    return 'red' as const;
  }, [pctCheck]);

  const walkaroundDateLabel = useMemo(() => {
    if (selectedPeriod === 'today') return 'Today';
    if (selectedPeriod === 'yesterday') return 'Yesterday';
    const from = toDateLabel(getDateRange.startDate.toISOString());
    const to = toDateLabel(getDateRange.endDate.toISOString());
    if (!from) return '';
    if (!to || from === to) return from;
    return `${from} to ${to}`;
  }, [selectedPeriod, getDateRange]);

  // Scroll to walkaround table when Walkaround Checks KPI / deep links fire.
  useEffect(() => {
    if (!highlightKPI || (highlightKPI !== 'walkaround' && highlightKPI !== 'check-compliance')) return;
    const timer = setTimeout(() => {
      walkaroundTableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      onHighlightConsumed?.();
    }, 400);
    return () => clearTimeout(timer);
  }, [highlightKPI, onHighlightConsumed]);

  // Walkaround-check table rows — only recompute when the check data or filter change,
  // not on every render (e.g. every keystroke in the driver search box below).
  const [selectedCheckRowId, setSelectedCheckRowId] = useState<string | null>(null);
  const walkaroundRows = useMemo(() => {
    const rows = shownChecks?.rows || [];
    return (checkStatusFilter === 'all'
      ? rows
      : rows.filter((r) => rowMatchesCheckFilter(r, checkStatusFilter))
    ).map((r, i) => {
      const id = `${r.day || 'day'}-${r.driver_key}-${i}`;
      const expands = Number(r.defect_count ?? 0) > 0;
      return {
      id,
      day: toWalkaroundDayLabel(r.day),
      day_sortKey: toDateLabel(r.day) || '',
      // Rows with defects expand: a chevron before the name, turned down when open.
      driver_name: expands ? (
        <span className="inline-flex items-center gap-1">
          <ChevronRight
            className={`w-4 h-4 shrink-0 text-chart-5 transition-transform ${selectedCheckRowId === id ? 'rotate-90' : ''}`}
            aria-hidden
          />
          {formatDriverDisplayName(r.driver_name) || r.driver_key}
        </span>
      ) : (
        formatDriverDisplayName(r.driver_name) || r.driver_key
      ),
      driver_name_sortKey: formatDriverDisplayName(r.driver_name) || r.driver_key,
      vehicle: r.vehicle || '—',
      shift_start_at: formatTimeHm(r.shift_start_instant, shiftStartOf(r)),
      check_started_at: formatTimeHm(r.check_started_instant, r.check_started_at),
      check_ended_at: formatTimeHm(r.check_ended_instant, r.check_ended_at),
      duration: r.short_check ? (
        <span className="font-semibold text-destructive">
          {formatCheckDuration(r.check_duration_minutes)} (short)
        </span>
      ) : (
        <span className={r.check_duration_minutes != null ? 'text-success' : 'text-muted-foreground'}>
          {formatCheckDuration(r.check_duration_minutes ?? null)}
        </span>
      ),
      duration_sortKey: r.check_duration_minutes ?? -1,
      status:
        isPerformedCheck(r.status) ? (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-success/40 text-success border border-success/60">
            Checked
          </span>
        ) : r.status === 'missing' ? (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-destructive/40 text-destructive border border-destructive/60">
            Missing
          </span>
        ) : r.status === 'unverified' ? (
          <span
            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-card/80 text-muted-foreground border border-dashed border-accent"
            title="Not counted: not in one of your driver groups (or, where none are set, no tachograph card on Samsara's driver list) and no recent walkaround checks, so they cannot be confirmed as a driver"
          >
            Not verified
          </span>
        ) : (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-secondary/60 text-foreground border border-border">
            No drive
          </span>
        ),
      status_sortKey:
        r.status === 'unverified'
          ? 4
          : r.status === 'missing'
          ? 0
          : r.short_check
            ? 1
            : isPerformedCheck(r.status)
              ? 2
              : 3,
      defect_count:
        (r.defect_count ?? 0) > 0 ? (
          <span className="text-warning font-semibold">{r.defect_count}</span>
        ) : (
          <span className="text-muted-foreground">0</span>
        ),
      defect_count_sortKey: Number(r.defect_count ?? 0),
      _check: r,
      // Only a missing check is highlighted.
      ...(r.status === 'missing' ? { _rowAccent: 'red' as const } : {}),
    };
    });
  }, [shownChecks, checkStatusFilter, selectedCheckRowId]);
  // The row whose defects are shown; cleared when the rows change under it.
  const selectedCheckRow = useMemo(
    () => (walkaroundRows.find((row) => row.id === selectedCheckRowId)?._check ?? null) as CheckComplianceRow | null,
    [walkaroundRows, selectedCheckRowId],
  );

  // Multi-day windows (Last 7 days / MTD / Last month) need a Date column so
  // each driver×day row is distinguishable; Today/Yesterday already imply the day.
  const walkaroundColumns = useMemo(
    () => [
      { key: 'driver_name', label: 'Driver', align: 'left' as const },
      { key: 'vehicle', label: 'Vehicle', align: 'left' as const },
      ...(!isSingleDayPeriod
        ? [{ key: 'day', label: 'Date', align: 'left' as const }]
        : []),
      { key: 'shift_start_at', label: 'Shift start', align: 'left' as const },
      { key: 'check_started_at', label: 'Check Start', align: 'left' as const },
      { key: 'check_ended_at', label: 'Check End', align: 'left' as const },
      { key: 'duration', label: 'Duration', align: 'left' as const },
      { key: 'status', label: 'Status', align: 'left' as const },
      { key: 'defect_count', label: 'Defects', align: 'right' as const },
    ],
    [isSingleDayPeriod],
  );

  // Auto-open KPI modal once when navigated from Control Centre alerts
  useEffect(() => {
    if (!highlightKPI || !displayData) return;
    if (highlightKPI === 'walkaround' || highlightKPI === 'check-compliance') return;
    const timer = setTimeout(() => {
      kpiGridRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      if (highlightKPI === 'violations') {
        setKpiModal({
          open: true,
          title: 'Driver Violations',
          subtitle: `${displayData?.infringement_count?.toLocaleString() || 0} total violations`,
          filterFn: (d: DriverModalRow) => (d.violationCount || 0) > 0,
          defaultSort: MOST_VIOLATIONS_FIRST,
        });
      } else if (highlightKPI === 'at-risk') {
        setKpiModal({
          open: true,
          title: 'At Risk Drivers',
          subtitle: 'Drivers flagged at-risk or non-compliant',
          filterFn: isAtRiskDriver,
        });
      } else {
        setKpiModal({ open: true, title: 'All Drivers', subtitle: `${totalDrivers} drivers monitored` });
      }
      onHighlightConsumed?.();
    }, 400);
    return () => clearTimeout(timer);
  }, [highlightKPI, displayData, totalDrivers, onHighlightConsumed]);

  // If no data and not loading, show fetch prompt
  if (!sourceData && !showLoading && !shownChecks && !checksLoading) {
    return (
      <div className="text-center py-12">
        <Users className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-white mb-2">Driver Safety & Compliance</h3>
        <p className="text-muted-foreground mb-6">
          Monitor driver hours, safety compliance, and identify areas for improvement
          {driverSourceLabel ? ` (${driverSourceLabel})` : ''}
        </p>
        {periodError && <p className="text-destructive mb-4 text-sm">{periodError}</p>}
        <Button
          onClick={handleRefresh}
          loading={showLoading}
          size="lg"
          className="rounded-lg"
        >
          {showLoading
            ? (driverSourceLabel ? `Loading (${driverSourceLabel})...` : 'Loading...')
            : 'Load Driver Data'}
        </Button>
      </div>
    );
  }

  // While period-scoped data is in flight, show a loading shell — never paint
  // Math.max-padded "fake" donut slices (equal 0.1 / 1 values) over empty data.
  if (showLoading && !displayData && !shownChecks) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h3 className="text-2xl font-bold text-white flex items-center gap-3">
                <Shield className="w-7 h-7 text-success" />
                Driver Safety Intelligence
              </h3>
              <p className="text-muted-foreground mt-1">{driverComplianceSubtitle(driverSourceIds)}</p>
            </div>
            <ControlCentreSourceBadge label={driverSourceLabel} />
          </div>
          <div className="flex items-center justify-between gap-4 flex-wrap w-full">
            <ControlCentrePeriodPanel
              value={selectedPeriod}
              onChange={setSelectedPeriod}
              options={DRIVER_CHECK_PERIOD_OPTIONS}
              accent="green"
              ariaLabel="Walkaround checks time period"
            />
            {!useMockData && (
              <ControlCentreDailyChecksPdfExport
                driverSourceLabel={driverSourceLabel}
                from={selectedFrom}
                to={selectedTo}
                disabled={checksLoading}
              />
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground" role="status" aria-live="polite">
          <RefreshCw className="w-4 h-4 animate-spin text-success" />
          {periodPreparing || checksPreparing
            ? STORED_VIEW_PREPARING_MESSAGE
            : driverSourceLabel ? `Loading driver data (${driverSourceLabel})…` : 'Loading driver data…'}
        </div>
        {/* The brief loads on its own for the selected period (with its own skeleton). */}
        {!useMockData && (
          <DailySafetyBriefPanel from={selectedFrom} to={selectedTo} scope="driver" onNavigate={handleBriefNavigate} />
        )}
        <DriverTabSkeleton />
      </div>
    );
  }

  // Calculate insights from filtered/display data
  const compliancePercentage = displayData?.compliance_percentage || 0;
  const compliantDrivers = displayData?.driver_hours_compliant || 0;
  const notEvaluatedDrivers = displayData?.driver_hours_not_evaluated || 0;
  const evaluatedDrivers = Math.max(0, totalDrivers - notEvaluatedDrivers);
  const nonCompliantDrivers = Math.max(0, evaluatedDrivers - compliantDrivers);

  // Compares evaluated drivers only. Not-evaluated drivers (e.g. no tacho
  // data) are stated in a caption under the chart, never silently dropped.
  const complianceBreakdown = [
    { label: 'Compliant', value: compliantDrivers, color: STATUS_COLORS.success },
    { label: 'Non-Compliant', value: nonCompliantDrivers, color: STATUS_COLORS.destructive },
  ];

  const totalDrivingHours = displayData?.total_driving_hours || 0;
  const totalRestHours = displayData?.total_rest_hours || 0;
  const totalWorkHours = displayData?.total_work_hours || 0;
  const totalUnknownHours = displayData?.total_unknown_hours || 0;
  const totalShiftHours = displayData?.total_shift_hours || 0;
  // T&A only supplies clocked shift duration — no drive/rest/work split. Chart that instead of an empty donut.
  const hasActivityTimeSplit =
    totalDrivingHours > 0 || totalRestHours > 0 || totalWorkHours > 0 || totalUnknownHours > 0;
  // Real open-shift signal (shift_end not yet recorded) rather than guessing from
  // period + zero totals — a driver with no shift today is not "still updating".
  const openShiftCount = displayData?.open_shift_count ?? 0;
  const hoursPendingForToday =
    viewingLiveToday && !hasActivityTimeSplit && totalShiftHours <= 0 && openShiftCount > 0;
  // Mutually exclusive tachograph slices (no Available Time).
  const activityDistribution = hasActivityTimeSplit
    ? [
        { label: 'Driving Time', value: totalDrivingHours, color: CHART_PALETTE[2] },
        { label: 'Work Time', value: totalWorkHours, color: CHART_PALETTE[1] },
        { label: 'Break Time', value: totalRestHours, color: CHART_PALETTE[0] },
        { label: 'Unknown', value: totalUnknownHours, color: CHART_PALETTE[4] },
      ]
    : totalShiftHours > 0
      ? [{ label: 'Shift Hours (T&A)', value: totalShiftHours, color: CHART_PALETTE[4] }]
      : [
          { label: 'Driving Time', value: 0, color: CHART_PALETTE[2] },
          { label: 'Work Time', value: 0, color: CHART_PALETTE[1] },
          { label: 'Break Time', value: 0, color: CHART_PALETTE[0] },
          { label: 'Unknown', value: 0, color: CHART_PALETTE[4] },
        ];
  // Drivers whose hours have a tachograph breakdown: the only ones in the split.
  const tachoSplitDrivers = (displayData?.driver_details || []).filter(
    (d) => (d.driving_hours || 0) + (d.work_hours || 0) + (d.rest_hours || 0) + (d.unknown_hours || 0) > 0,
  ).length;
  const timeAllocationSubtitle = hasActivityTimeSplit
    // Today covers only the part of each shift so far (mostly loading and
    // checks early on), so it is labelled as such.
    ? `${viewingLiveToday ? 'So far today' : 'How drivers spent their time'}, for ${tachoSplitDrivers} ${
        tachoSplitDrivers === 1 ? 'driver' : 'drivers'} with tachograph data`
    : totalShiftHours > 0
      ? 'Clocked shift hours from T&A (no drive/rest split available)'
      : 'How drivers spend their time';
  const noTachoDrivers = (displayData?.driver_details || []).filter(
    (d) => d.compliance_status === 'unknown' && notEvaluatedLabel(d) === 'No tacho data',
  ).length;
  const otherNotEvaluated = Math.max(0, notEvaluatedDrivers - noTachoDrivers);
  // Each part opens its own table filter.
  const notEvaluatedCaptionParts: Array<{ text: string; filter: DriverTableFilter }> = [
    ...(noTachoDrivers > 0
      ? [{ text: `${noTachoDrivers} ${noTachoDrivers === 1 ? 'has' : 'have'} no tacho data`, filter: 'no-tacho' as const }]
      : []),
    ...(otherNotEvaluated > 0
      ? [{ text: `${otherNotEvaluated} could not be fully checked`, filter: 'not-evaluated' as const }]
      : []),
  ];
  const complianceSubtitle = evaluatedDrivers > 0
    ? `${compliantDrivers} of ${evaluatedDrivers} evaluated drivers compliant`
    : 'No drivers evaluated';

  const driverDetailsForFilters: DriverDetail[] = displayData?.driver_details || [];
  const driverFilterCounts = {
    all: driverDetailsForFilters.length,
    compliant: driverDetailsForFilters.filter((d) => rowMatchesDriverTableFilter(d, 'compliant')).length,
    'non-compliant': driverDetailsForFilters.filter((d) => rowMatchesDriverTableFilter(d, 'non-compliant')).length,
    'no-tacho': driverDetailsForFilters.filter((d) => rowMatchesDriverTableFilter(d, 'no-tacho')).length,
    'not-evaluated': driverDetailsForFilters.filter((d) => rowMatchesDriverTableFilter(d, 'not-evaluated')).length,
    'unknown-hours': driverDetailsForFilters.filter((d) => rowMatchesDriverTableFilter(d, 'unknown-hours')).length,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-4">
          <div>
            <h3 className="text-2xl font-bold text-white flex items-center gap-3">
              <Shield className="w-7 h-7 text-success" />
              Driver Safety Intelligence
            </h3>
            <p className="text-muted-foreground mt-1">{driverComplianceSubtitle(driverSourceIds)}</p>
        </div>
          <ControlCentreSourceBadge label={driverSourceLabel} />
      </div>

        <div className="flex flex-col gap-1">
          <div className="flex items-center justify-between gap-4 flex-wrap w-full">
            <ControlCentrePeriodPanel
              value={selectedPeriod}
              onChange={setSelectedPeriod}
              options={DRIVER_CHECK_PERIOD_OPTIONS}
              accent="green"
              ariaLabel="Walkaround checks time period"
            />
            {!useMockData && (
              <ControlCentreDailyChecksPdfExport
                driverSourceLabel={driverSourceLabel}
                from={selectedFrom}
                to={selectedTo}
                disabled={checksLoading}
              />
            )}
          </div>
          {/* Last successful scheduled fetch of the tab's feeds (oldest of
              them), not when the page loaded. Hidden when it cannot be stated. */}
          {!useMockData && periodDataMatchesSelection && periodData?.data_current_as_of && !periodError && (
            <div className="text-sm text-foreground min-w-0">
              Data current as of {formatDataCurrentAs(periodData.data_current_as_of)}
            </div>
          )}
          {!useMockData && periodDataMatchesSelection && !periodError
            && storedViewStaleMessage(periodData?.projection) && (
            <div className="text-sm text-warning min-w-0">
              {storedViewStaleMessage(periodData?.projection)}
            </div>
          )}
        </div>
      </div>

      {!useMockData && periodDataRange && (
        <DailySafetyBriefPanel
          from={selectedFrom}
          to={selectedTo}
          scope="driver"
          onNavigate={handleBriefNavigate}
          refreshKey={periodLoadCount}
        />
      )}

      {!shownChecks && (checksLoading || checksError) && (
        <div className="space-y-3" role="status" aria-live="polite">
          {checksError ? (
            <p className="text-sm text-destructive">{checksError}</p>
          ) : (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <RefreshCw className="w-4 h-4 animate-spin text-success" />
              {checksPreparing ? STORED_VIEW_PREPARING_MESSAGE : 'Loading walkaround checks…'}
            </div>
          )}
          {!checksError && <ChecksSectionSkeleton />}
        </div>
      )}

      {shownChecks && (
        <div className="space-y-3 scroll-mt-20" ref={walkaroundTableRef}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="flex flex-col items-stretch justify-start gap-2 whitespace-nowrap rounded-lg border-2 border-destructive bg-destructive/30 px-3 py-2 text-left font-normal transition-colors hover:bg-destructive/30">
              <p className="text-lg text-muted-foreground">Missing check</p>
              <p className="text-3xl font-semibold text-destructive">{shownChecks.kpis.missing}</p>
            </div>
            <div className="flex flex-col items-stretch justify-start gap-2 whitespace-nowrap rounded-lg border-2 border-border bg-card/60 px-3 py-2 text-left font-normal transition-colors hover:border-warning/60 hover:bg-card/60">
              <p className="text-lg text-muted-foreground">Checked under 15 min</p>
              <p className="text-3xl font-semibold text-warning">{shortCheckCount}</p>
            </div>
            <div
              className="flex flex-col items-stretch justify-start gap-2 whitespace-nowrap rounded-lg border-2 border-border bg-card/60 px-3 py-2 text-left font-normal transition-colors hover:border-chart-5/60 hover:bg-card/60"
              title="Walkaround checks that recorded at least one defect"
            >
              <p className="text-lg text-muted-foreground">Defects found</p>
              <p className="text-3xl font-semibold text-chart-5">{defectsFoundCount}</p>
            </div>
            <div
              className={cn(
                'flex flex-col items-stretch justify-start gap-2 whitespace-nowrap rounded-lg border-2 px-3 py-2 text-left font-normal transition-colors',
                walkaroundPctTone === 'green'
                  ? 'border-border bg-card/60 hover:border-success/60 hover:bg-card/60'
                  : walkaroundPctTone === 'amber'
                    ? 'border-warning/60 bg-warning/20 hover:border-warning hover:bg-warning/20'
                    : 'border-destructive/60 bg-destructive/20 hover:border-destructive hover:bg-destructive/20',
              )}
            >
              <p className="text-lg text-muted-foreground">% check</p>
              <p
                className={`text-3xl font-semibold ${
                  walkaroundPctTone === 'green'
                    ? 'text-success'
                    : walkaroundPctTone === 'amber'
                      ? 'text-warning'
                      : 'text-destructive'
                }`}
              >
                {pctCheck}%
              </p>
            </div>
          </div>

          <DataTable
            title={
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between w-full">
                <div className="flex items-center gap-2 shrink-0">
                  <CheckCircle className="w-5 h-5 text-success" />
                  <span>
                    Driver walkaround check
                    {walkaroundDateLabel ? ` (${walkaroundDateLabel})` : ''}
                  </span>
                </div>
                <div className="flex flex-wrap gap-1 justify-end sm:ml-auto">
                  {([
                    { key: 'all' as const, label: 'All', active: 'bg-secondary/60 border-muted-foreground text-foreground hover:bg-secondary/60', idle: 'hover:border-border hover:text-foreground' },
                    { key: 'missing' as const, label: 'Missing', active: 'bg-destructive/40 border-destructive text-destructive hover:bg-destructive/40', idle: 'hover:border-destructive/50 hover:text-destructive' },
                    { key: 'short' as const, label: 'Under 15 min', active: 'bg-warning/40 border-warning text-warning hover:bg-warning/40', idle: 'hover:border-warning/50 hover:text-warning' },
                    { key: 'defects' as const, label: 'Defects', active: 'bg-chart-5/40 border-chart-5 text-chart-5 hover:bg-chart-5/40', idle: 'hover:border-chart-5/50 hover:text-chart-5' },
                  ]).map(({ key, label, active: activeCls, idle }) => {
                    const active = checkStatusFilter === key;
                    return (
                      <Button
                        key={key}
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => applyCheckStatusFilter(key)}
                        className={cn(
                          'h-auto rounded border px-2.5 py-1 text-sm',
                          active ? activeCls : `bg-card border-border text-muted-foreground hover:bg-card ${idle}`,
                        )}
                      >
                        {label}
                      </Button>
                    );
                  })}
                </div>
              </div>
            }
            hoverColor="green"
            hideExport
            maxRows={10}
            emptyMessage="No data preview"
            defaultSort={{ column: 'status', direction: 'asc' }}
            columns={walkaroundColumns}
            data={walkaroundRows}
            onRowClick={(row) => {
              const checkRow = row as WalkaroundTableRow;
              const id = String(checkRow.id);
              setSelectedCheckRowId((cur) => (cur === id || Number(checkRow._check?.defect_count ?? 0) === 0 ? null : id));
            }}
            selectedRowId={selectedCheckRow ? selectedCheckRowId : null}
            expandedRowId={selectedCheckRow ? selectedCheckRowId : null}
            renderExpandedRow={(row) => {
              const checkRow = row as WalkaroundTableRow;
              return Number(checkRow._check?.defect_count ?? 0) > 0
                ? <WalkaroundDefectDetails row={checkRow._check as CheckComplianceRow} />
                : null;
            }}
          />
        </div>
      )}

      {/* Drivers' hours and violations: its own period, since tacho data is a day behind the checks above. */}
      <div className="flex flex-col gap-2 border-t border-border pt-6">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h4 className="text-xl font-semibold text-foreground">Drivers' hours and violations</h4>
            <p className="text-sm text-muted-foreground">
              From tacho data, which is about a day behind: a shift shows once the driver's next shift starts.
            </p>
            {awaitingNextShiftCount > 0 && (
              <p className="text-sm text-warning mt-1">
                {awaitingNextShiftCount} of {totalDrivers} drivers waiting for their next shift: their latest day may still change.
              </p>
            )}
          </div>
          <ControlCentrePeriodPanel
            value={hoursPeriod}
            onChange={setHoursPeriod}
            options={DRIVER_HOURS_PERIOD_OPTIONS}
            accent="green"
            ariaLabel="Drivers' hours time period"
          />
        </div>
      </div>

      {!displayData ? (
        <div className="space-y-3" role="status" aria-live="polite">
          {periodError ? (
            <p className="text-sm text-destructive">{periodError}</p>
          ) : (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <RefreshCw className="w-4 h-4 animate-spin text-success" />
              {periodPreparing ? STORED_VIEW_PREPARING_MESSAGE : "Loading drivers' hours…"}
            </div>
          )}
          {!periodError && <HoursSectionSkeleton />}
        </div>
      ) : (<>
      {/* Primary KPI Metrics — equal-height cards in one row. The walkaround
          check rate is not repeated here: the "% check" tile above the
          Walkaround Check table already shows it. */}
      <div ref={kpiGridRef} className="grid grid-cols-1 md:grid-cols-2 gap-4 items-stretch">
        <ControlCentreKPICard
          id="kpi-compliance"
          className="h-full"
          title="Compliance"
          value={`${compliancePercentage}%`}
          subtitle={
            notEvaluatedDrivers > 0
              ? `${compliantDrivers} of ${evaluatedDrivers} evaluated · ${notEvaluatedDrivers} not evaluated`
              : `${compliantDrivers} of ${totalDrivers} drivers`
          }
          icon={Shield}
          variant="primary"
          colorScheme={compliancePercentage >= 90 ? 'green' : compliancePercentage >= 70 ? 'amber' : 'red'}
          onClick={() => setKpiModal({ open: true, title: 'Driver Compliance', subtitle: `${compliantDrivers} of ${evaluatedDrivers} evaluated drivers compliant` })}
        />

        <ControlCentreKPICard
          id="kpi-violations"
          className="h-full"
          title="Violations"
          value={displayData?.infringement_count?.toLocaleString() || 0}
          subtitle="Click to view all violations"
          icon={FileWarning}
          variant="primary"
          colorScheme="red"
          onClick={() => setKpiModal({ open: true, title: 'Driver Violations', subtitle: `${displayData?.infringement_count?.toLocaleString() || 0} total violations`, filterFn: (d: DriverModalRow) => (d.violationCount || 0) > 0, defaultSort: MOST_VIOLATIONS_FIRST })}
        />
      </div>

      {/* Analytics Charts — same card chrome / green glow as walkaround DataTable */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Compliance Status Chart */}
        <div className="bg-card rounded-lg shadow-sm overflow-visible border border-border hover:border-success hover:shadow-lg hover:shadow-success/30 transition-all group p-2">
          <div className="rounded-lg px-4 py-4 flex flex-col">
            <div className="flex items-center gap-2 mb-1">
              <CheckCircle className="w-5 h-5 text-success" />
              <h4 className="text-lg font-semibold text-white">Compliance Status</h4>
            </div>
            <p className="text-sm text-muted-foreground mb-4">{complianceSubtitle}</p>
            <div className="h-64 min-h-0 flex-1">
              {evaluatedDrivers > 0 ? (
                <DonutChart
                  data={complianceBreakdown}
                  title=""
                  dense
                  valueUnit="driver"
                  showValuesInLegend
                  hoverTooltip={false}
                />
              ) : (
                <div className="h-full flex items-center justify-center text-sm text-muted-foreground text-center px-4">
                  {totalDrivers > 0
                    ? 'No drivers evaluated. None have tacho data the hours rules can check.'
                    : 'No drivers in this period.'}
                </div>
              )}
            </div>
            {notEvaluatedDrivers > 0 && (
              <p className="mt-3 text-sm text-muted-foreground">
                Based on {evaluatedDrivers} of {totalDrivers} drivers.{' '}
                {notEvaluatedCaptionParts.map((part, i) => (
                  <span key={part.filter}>
                    {i > 0 && ', '}
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        applyDriverTableFilter(part.filter);
                        driverTableFiltersRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }}
                      className="h-auto px-0 text-left text-sm underline-offset-2 hover:bg-transparent hover:underline"
                    >
                      {part.text}
                    </Button>
                  </span>
                ))}
                .
              </p>
            )}
          </div>
        </div>

        {/* Time Allocation Chart */}
        <div className="bg-card rounded-lg shadow-sm overflow-visible border border-border hover:border-success hover:shadow-lg hover:shadow-success/30 transition-all group p-2">
          <div className="rounded-lg px-4 py-4 flex flex-col">
            <div className="flex items-center gap-2 mb-1">
              <Activity className="w-5 h-5 text-chart-5" />
              <h4 className="text-lg font-semibold text-white">Time Allocation</h4>
            </div>
            {!hoursPendingForToday && (
              <p className="text-sm text-muted-foreground mb-4">{timeAllocationSubtitle}</p>
            )}
            <div className="h-64 min-h-0 flex-1">
              {hoursPendingForToday ? (
                <div className="h-full flex flex-col items-center justify-center text-center px-6 rounded-lg border border-dashed border-chart-5/40 bg-background/40">
                  <Clock className="w-10 h-10 text-chart-5/80 mb-3" aria-hidden />
                  <p className="text-base font-medium text-white">Hours still updating</p>
                  <p className="text-sm text-muted-foreground mt-2 max-w-sm leading-relaxed">
                    Drivers are still on shift. Time split and shift totals fill in after
                    clock-out — check back later today or switch to Yesterday for a full day.
                  </p>
                </div>
              ) : (
                <DonutChart
                  data={activityDistribution}
                  title=""
                  dense
                  valueUnit="hour"
                  showValuesInLegend
                  hoverTooltip={false}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Driver Details — same green-glow card margin as walkaround DataTable */}
      <div className="bg-card rounded-lg shadow-sm overflow-visible border border-border hover:border-success hover:shadow-lg hover:shadow-success/30 transition-all group p-2">
        <div className="rounded-lg p-6 md:p-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
          <div>
            <h4 className="text-xl font-bold text-white flex items-center gap-2">
              <Users className="w-6 h-6 text-success" />
              Driver Performance Details
            </h4>
            <p className="text-sm text-muted-foreground mt-1">
              {viewingLiveToday
                ? 'Shift hours may show In progress until drivers clock out today'
                : 'View individual driver compliance and performance metrics'}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-50">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search drivers..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1); // Reset to first page when search changes
                }}
                className="w-full pl-10 pr-4 py-2.5 bg-secondary/80 border border-border rounded-lg text-sm text-white placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all"
              />
            </div>
          </div>
        </div>

        {(displayData?.rule_pack_label || displayData?.rule_pack_id) && (
          <p className="text-xs text-muted-foreground mb-2">
            Rules: {displayData.rule_pack_label || displayData.rule_pack_id}
            {displayData.rule_pack_version ? ` · v${displayData.rule_pack_version}` : ''}
          </p>
        )}

        {/* Status filters — before column headers (same chip pattern as walkaround) */}
        <div ref={driverTableFiltersRef} className="flex flex-wrap gap-1 mb-3 scroll-mt-20">
          {([
            { key: 'all' as const, label: 'All', active: 'bg-secondary/60 border-muted-foreground text-foreground hover:bg-secondary/60', idle: 'hover:border-border hover:text-foreground' },
            {
              key: 'non-compliant' as const,
              label: 'Non-Compliant',
              active: 'bg-destructive/40 border-destructive text-destructive hover:bg-destructive/40',
              // Count cue only — no fill, so it never looks like a second selected filter.
              alert: 'bg-card border-destructive text-destructive',
              idle: 'hover:border-destructive/50 hover:text-destructive',
            },
            { key: 'compliant' as const, label: 'Compliant', active: 'bg-success/40 border-success text-success hover:bg-success/40', idle: 'hover:border-success/50 hover:text-success' },
            {
              key: 'no-tacho' as const,
              label: 'No tacho data',
              active: 'bg-secondary/60 border-muted-foreground text-foreground hover:bg-secondary/60',
              idle: 'hover:border-border hover:text-foreground',
            },
            {
              // Tacho data the hours rules could not fully check. Shown only when
              // there are some, so operators usually see "No tacho data" alone.
              key: 'not-evaluated' as const,
              label: 'Not evaluated',
              active: 'bg-secondary/60 border-muted-foreground text-foreground hover:bg-secondary/60',
              idle: 'hover:border-border hover:text-foreground',
              hideWhenEmpty: true,
            },
            {
              key: 'unknown-hours' as const,
              label: 'Unknown Hours',
              active: 'bg-chart-5/40 border-chart-5 text-chart-5 hover:bg-chart-5/40',
              alert: 'bg-card border-chart-5 text-chart-5',
              idle: 'hover:border-chart-5/50 hover:text-chart-5',
            },
          ] as Array<{
            key: DriverTableFilter;
            label: string;
            active: string;
            idle: string;
            alert?: string;
            hideWhenEmpty?: boolean;
          }>).map(({ key, label, active: activeCls, idle, alert, hideWhenEmpty }) => {
            const selected = driverTableFilter === key;
            const count = driverFilterCounts[key];
            if (hideWhenEmpty && count === 0 && !selected) return null;
            const showAlert = Boolean(alert) && count > 0 && !selected;
            return (
              <Button
                key={key}
                type="button"
                variant="secondary"
                size="sm"
                aria-pressed={selected}
                onClick={() => applyDriverTableFilter(key)}
                className={cn(
                  'h-auto rounded border px-2.5 py-1 text-sm',
                  selected
                    ? activeCls
                    : showAlert
                      ? `${alert} hover:bg-card ${idle}`
                      : `bg-card border-border text-muted-foreground hover:bg-card ${idle}`,
                )}
              >
                {label} ({count})
              </Button>
            );
          })}
        </div>

        {/* Driver Table — same scroll pane pattern as walkaround DataTable (overflow-y-auto → shared scrollbar CSS) */}
        <div
          className="overflow-y-auto overflow-x-auto"
          style={{
            maxHeight: `${40 + 10 * 42}px`,
            padding: '8px',
            paddingTop: 0,
            paddingBottom: '8px',
          }}
        >
          <table className="w-full">
            <thead className="sticky top-0 z-10 bg-card">
              <tr className="border-b border-border bg-white/10">
                <th className="w-2 py-3 p-0" aria-hidden />
                <th className="w-8 py-3 px-2" aria-label="Expand row" />
                <th className={sortHeaderClass('name')} onClick={() => handleSortHeaderClick('name')}>
                  <span className="inline-flex items-center gap-1">Driver {renderSortIcon('name')}</span>
                </th>
                <th className={sortHeaderClass('status')} onClick={() => handleSortHeaderClick('status')}>
                  <span className="inline-flex items-center gap-1">Status {renderSortIcon('status')}</span>
                </th>
                <th className={sortHeaderClass('shift')} onClick={() => handleSortHeaderClick('shift')}>
                  <span className="inline-flex items-center gap-1">Shift Hours {renderSortIcon('shift')}</span>
                </th>
                <th className={sortHeaderClass('driving')} onClick={() => handleSortHeaderClick('driving')}>
                  <span className="inline-flex items-center gap-1">Driving Hours {renderSortIcon('driving')}</span>
                </th>
                <th className={sortHeaderClass('work')} onClick={() => handleSortHeaderClick('work')}>
                  <span className="inline-flex items-center gap-1">Work Hours {renderSortIcon('work')}</span>
                </th>
                <th className={sortHeaderClass('rest')} onClick={() => handleSortHeaderClick('rest')}>
                  <span className="inline-flex items-center gap-1">Break Hours {renderSortIcon('rest')}</span>
                </th>
                <th className={sortHeaderClass('violations')} onClick={() => handleSortHeaderClick('violations')}>
                  <span className="inline-flex items-center gap-1">Violations {renderSortIcon('violations')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                // Use only real driver details from API - no synthetic data generation
                const driverDetails: DriverDetail[] = displayData?.driver_details || [];
                
                if (driverDetails.length === 0) {
                  // No data available - show empty state message
                  return (
                    <tr>
                      <td colSpan={9} className="py-12 text-center text-muted-foreground">
                        <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p>No driver data available</p>
                        <p className="text-sm mt-2">Please refresh to load driver compliance information.</p>
                      </td>
                    </tr>
                  );
                }
                
                // Filter by search query + status / unknown-hours chips
                const filtered = driverDetails.filter(driver =>
                  (
                    driver.driver_name.toLowerCase().includes(searchQuery.toLowerCase())
                    || (driver.emp_id && driver.emp_id.toLowerCase().includes(searchQuery.toLowerCase()))
                  )
                  && rowMatchesDriverTableFilter(driver, driverTableFilter),
                );
                
                // Show empty state if no drivers match search / filter
                if (filtered.length === 0) {
                  return (
                    <tr>
                      <td colSpan={9} className="py-12 text-center text-muted-foreground">
                        <Search className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p>
                          {searchQuery.trim()
                            ? `No drivers found matching "${searchQuery}"`
                            : 'No drivers match this filter'}
                        </p>
                        <p className="text-sm mt-2">
                          {searchQuery.trim()
                            ? 'Try adjusting your search query'
                            : 'Try another filter or All'}
                        </p>
                      </td>
                    </tr>
                  );
                }
                
                // Sort
                filtered.sort((a, b) => {
                  // Drivers whose hours were not evaluated (Not evaluated, then
                  // No tacho data) follow the evaluated ones on every column, in
                  // either direction: their status and hours are unknown, not 0.
                  const unevaluated = unevaluatedSortRank(a) - unevaluatedSortRank(b);
                  if (unevaluated !== 0) return unevaluated;
                  let comparison = 0;
                  switch (sortField) {
                    case 'name':
                      comparison = a.driver_name.localeCompare(b.driver_name);
                      break;
                    case 'status': {
                      // By severity: descending (the default) puts Non-Compliant
                      // first, then At Risk, then Compliant.
                      const severity = (s: string | undefined) =>
                        s === 'non-compliant' ? 3
                          : s === 'at-risk' ? 2
                          : s === 'compliant' ? 1
                          : 0;
                      comparison = severity(a.compliance_status) - severity(b.compliance_status);
                      break;
                    }
                    case 'shift':
                      comparison = (a.shift_hours || 0) - (b.shift_hours || 0);
                      break;
                    case 'driving':
                      comparison = (a.driving_hours || 0) - (b.driving_hours || 0);
                      break;
                    case 'work':
                      comparison = (a.work_hours || 0) - (b.work_hours || 0);
                      break;
                    case 'rest':
                      comparison = (a.rest_hours || 0) - (b.rest_hours || 0);
                      break;
                    case 'violations':
                    default:
                      comparison = (a.violations?.length || 0) - (b.violations?.length || 0);
                      break;
                  }
                  const directed = sortDirection === 'asc' ? comparison : -comparison;
                  if (directed !== 0) return directed;
                  // Ties: most violations first, then by name, in either direction.
                  return (b.violations?.length || 0) - (a.violations?.length || 0)
                    || a.driver_name.localeCompare(b.driver_name);
                });
                
                // Pagination
                const startIndex = (currentPage - 1) * rowsPerPage;
                const endIndex = startIndex + rowsPerPage;
                const paginatedDrivers = filtered.slice(startIndex, endIndex);
                
                return paginatedDrivers.map((driver, rowIndex) => {
                const statusTone: BadgeTone = driver.compliance_status === 'compliant' ? 'success' :
                                   driver.compliance_status === 'at-risk' ? 'warning' :
                                   driver.compliance_status === 'unknown' ? 'neutral' :
                                   'destructive';
                const statusBadge = driver.compliance_status === 'unknown'
                  ? notEvaluatedLabel(driver)
                  : complianceStatusLabel(driver.compliance_status || 'non-compliant');
                const hoursSource = driver.hours_source || 'activity';
                const hasActivityDetail = hoursSource === 'activity' || hoursSource === 'mixed';
                const shiftHours = Number(driver.shift_hours || 0);
                const isExpanded = expandedDriver?.driverId === driver.driver_id;
                // One person can come back as two rows with the same driver_id (a card number
                // stored with and without its 2-digit index); a duplicate key made React keep
                // stale copies on every filter change.
                const rowKey = `${driver.driver_id}|${driver.activity_entity_id ?? ''}|${rowIndex}`;
                const toggleExpanded = () => {
                  setExpandedDriver(
                    isExpanded
                      ? null
                      : {
                          driverId: driver.driver_id,
                          activityEntityId: driver.activity_entity_id,
                        },
                  );
                };
                const statusPill =
                  driver.compliance_status === 'compliant'
                    ? 'bg-success'
                    : driver.compliance_status === 'at-risk'
                      ? 'bg-warning'
                      : driver.compliance_status === 'unknown'
                        ? 'bg-muted-foreground'
                        : 'bg-destructive';
                const unknownHours = driver.unknown_hours != null ? Number(driver.unknown_hours) : 0;

                return (
                  <Fragment key={rowKey}>
                    <tr
                      className="border-b border-border/30 hover:bg-secondary/25 transition-colors duration-150 cursor-pointer"
                      onClick={toggleExpanded}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          toggleExpanded();
                        }
                      }}
                      tabIndex={0}
                      aria-expanded={isExpanded}
                      aria-label={`${isExpanded ? 'Collapse' : 'Expand'} details for ${driver.driver_name}`}
                      title={isExpanded ? 'Click to hide details' : 'Click to see details'}
                    >
                      <td className="relative p-0 w-2 align-middle">
                        <span
                          className={`absolute left-0 top-0 bottom-0 w-1 ${statusPill}`}
                          aria-hidden
                          title={statusBadge}
                        />
                      </td>
                      <td className="py-4 pl-2 pr-1 w-8">
                        <span className="inline-flex text-muted-foreground" aria-hidden>
                          {isExpanded ? (
                            <ChevronDown className="w-4 h-4" />
                          ) : (
                            <ChevronRight className="w-4 h-4" />
                          )}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <div className="font-semibold text-white">{driver.driver_name}</div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex flex-col items-start gap-1">
                          <Badge tone={statusTone} className="px-3 py-1.5 whitespace-nowrap">
                            {statusBadge}
                          </Badge>
                          {unknownHours > 0 && (
                            <span
                              className="text-xs font-medium text-chart-5 whitespace-nowrap"
                              title="Tachograph UNKNOWN state time — not drive, work, or break"
                            >
                              Unknown Hours: {unknownHours.toFixed(1)}h
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        {shiftHours > 0 ? (
                          <span className="text-white font-medium">{shiftHours.toFixed(1)}h</span>
                        ) : viewingLiveToday ? (
                          <span
                            className="inline-flex items-center gap-1.5 text-warning/90 text-sm font-medium"
                            title="Shift not closed yet — hours appear after clock-out"
                          >
                            <Clock className="w-3.5 h-3.5 shrink-0 opacity-80" aria-hidden />
                            In progress
                          </span>
                        ) : (
                          <span className="text-muted-foreground font-medium">—</span>
                        )}
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-2">
                          <span className="text-white font-medium">
                            {hasActivityDetail ? `${Number(driver.driving_hours || 0).toFixed(1)}h` : '—'}
                          </span>
                        {hasActivityDetail && driver.hours_compliance === false && (
                            <AlertTriangle className="w-4 h-4 text-destructive" />
                        )}
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <span className="text-white font-medium">
                          {hasActivityDetail ? `${Number(driver.work_hours || 0).toFixed(1)}h` : '—'}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <span className="text-white font-medium">
                          {hasActivityDetail ? `${Number(driver.rest_hours || 0).toFixed(1)}h` : '—'}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        {(driver.violations?.length || 0) > 0 ? (
                          <div className="flex items-center gap-1.5">
                            <FileWarning className="w-4 h-4 text-destructive" />
                            <span className="text-destructive font-semibold">{driver.violations?.length || 0}</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1.5">
                            <CheckCircle className="w-4 h-4 text-success" />
                            <span className="text-success font-semibold">0</span>
                          </div>
                        )}
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td colSpan={9} className="py-6 px-6 bg-gradient-to-r from-background/80 to-card/60">
                          <div
                            className="space-y-4"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {(driver.violations?.length || 0) > 0 && (
                              <div>
                                <h5 className="text-sm font-semibold text-white mb-2 flex items-center gap-2">
                                  <FileWarning className="w-4 h-4 text-destructive" />
                                  Violations ({driver.violations?.length || 0})
                                </h5>
                                {violationReviews.error ? (
                                  <p className="mb-2 text-xs text-warning">Couldn't load who resolved these violations ({violationReviews.error}).</p>
                                ) : null}
                                <div className="space-y-2">
                                  {(driver.violations || []).map((violation, idx) => {
                                    const severityColor = violation.severity === 'critical' ? 'bg-destructive/20 border-destructive/50 text-destructive' :
                                                         violation.severity === 'warning' ? 'bg-warning/20 border-warning/50 text-warning' :
                                                         'bg-ring/20 border-ring/50 text-ring';
                                    return (
                                      <div key={idx} className={`p-3 rounded border ${severityColor}`}>
                                        <div className="flex items-start justify-between gap-3">
                                          <div className="flex-1">
                                            <div className="font-medium">{violation.label || violation.description || violation.type}</div>
                                            {violation.description && violation.description !== (violation.label || violation.type) && (
                                              <div className="text-xs mt-1 opacity-90">{violation.description}</div>
                                            )}
                                            <div className="text-xs opacity-75 mt-1">Date: {violation.date}</div>
                                          </div>
                                          <ViolationReviewControl driverId={driver.driver_id} violation={violation} state={violationReviews} />
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}

                            {(driver.violations?.length || 0) === 0 && (
                              driver.evaluation_gaps?.includes('no_tachograph_activity') ? (
                                <div className="p-3 bg-accent/10 border border-accent/30 rounded text-foreground text-sm">
                                  No tachograph data for this driver in this window, only
                                  time-and-attendance clock-ins. Driving hours and rest were not evaluated.
                                </div>
                              ) : driver.compliance_status === 'unknown' ? (
                                <div className="p-3 bg-accent/10 border border-accent/30 rounded text-foreground text-sm">
                                  No violations found in the checks that ran, but some checks could not be
                                  run for this window, so this driver is not marked compliant.
                                </div>
                              ) : (
                                <div className="p-3 bg-success/10 border border-success/30 rounded text-success text-sm">
                                  ✓ No violations recorded for this driver
                                </div>
                              )
                            )}

                            {driver.awaiting_next_shift && (
                              <div className="p-3 bg-warning/10 border border-warning/30 rounded text-foreground text-sm">
                                Waiting for this driver's next shift: their latest day in this period may still change.
                              </div>
                            )}
                            {!isSingleDayPeriod && skippedCheckGaps(driver).length > 0
                              && !driver.evaluation_gaps!.includes('no_tachograph_activity') && (
                              <div className="p-3 bg-accent/10 border border-accent/30 rounded text-foreground text-sm">
                                Some weekly / working-time checks were not run for this window
                                {' '}({skippedCheckGaps(driver).join(', ')}).
                                Hours and Rest above reflect rules that were checked for this period.
                              </div>
                            )}

                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                              <div>
                                <span className="text-muted-foreground">Last Activity:</span>
                                <div className="text-white">
                                  {formatLastActivity(driver.last_activity, driver.last_activity_instant)}
                                </div>
                              </div>
                              <div>
                                <span className="text-muted-foreground">Hours Compliance:</span>
                                <div className={
                                  !hasActivityDetail || driver.hours_compliance == null
                                    ? 'text-muted-foreground'
                                    : (driver.hours_compliance ? 'text-success' : 'text-destructive')
                                }>
                                  {!hasActivityDetail
                                    ? 'N/A (shift hours only)'
                                    : driver.hours_compliance == null
                                      ? 'Not evaluated'
                                      : (driver.hours_compliance ? '✓ Compliant' : '✗ Non-Compliant')}
                                </div>
                              </div>
                              <div>
                                <span className="text-muted-foreground">Rest Compliance:</span>
                                <div className={
                                  !hasActivityDetail || driver.rest_compliance == null
                                    ? 'text-muted-foreground'
                                    : (driver.rest_compliance ? 'text-success' : 'text-warning')
                                }>
                                  {!hasActivityDetail
                                    ? 'N/A (shift hours only)'
                                    : driver.rest_compliance == null
                                      ? 'Not evaluated'
                                      : (driver.rest_compliance ? '✓ Compliant' : '⚠ Needs Review')}
                                </div>
                              </div>
                              {unknownHours > 0 && (
                                <div>
                                  <span className="text-muted-foreground">Unknown Hours:</span>
                                  <div
                                    className="text-chart-5"
                                    title="Tachograph UNKNOWN state time: not drive, work or break"
                                  >
                                    {unknownHours.toFixed(1)}h
                                  </div>
                                </div>
                              )}
                            </div>

                            <div>
                              <h5 className="text-lg font-semibold text-white mb-2 flex items-center gap-2">
                                Vehicles driven
                              </h5>
                              {vehicleAssignments?.driverId === driver.driver_id && vehicleAssignments.loading && (
                                <div className="text-sm text-muted-foreground flex items-center gap-2">
                                  <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                                  Loading vehicle assignments…
                                </div>
                              )}
                              {vehicleAssignments?.driverId === driver.driver_id && vehicleAssignments.error && (
                                <div className="text-sm text-warning/90">
                                  {vehicleAssignments.error}
                                </div>
                              )}
                              {vehicleAssignments?.driverId === driver.driver_id
                                && !vehicleAssignments.loading
                                && vehicleAssignments.rows.length === 0
                                && (vehicleAssignments.error == null
                                  || vehicleAssignments.error.includes('Some vehicle lookups'))
                                && (
                                <div className="text-sm text-muted-foreground">
                                  No vehicle assignment recorded for this period
                                </div>
                              )}
                              {vehicleAssignments?.driverId === driver.driver_id
                                && !vehicleAssignments.loading
                                && vehicleAssignments.rows.length > 0 && (
                                <div className="overflow-x-auto rounded border border-border/60">
                                  <table className="w-full text-sm">
                                    <thead>
                                      <tr className="bg-card/80 text-muted-foreground text-left">
                                        <th className="py-2 px-3 font-medium">Vehicle</th>
                                        <th className="py-2 px-3 font-medium">Start</th>
                                        <th className="py-2 px-3 font-medium">End</th>
                                        <th className="py-2 px-3 font-medium text-right">Duration</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {vehicleAssignments.rows.map((row) => (
                                        <tr
                                          key={`${row.vehicle_entity_id}-${row.start}-${row.end ?? 'open'}`}
                                          className="border-t border-border/40 text-foreground"
                                        >
                                          <td className="py-2 px-3 font-medium text-white">
                                            {row.vehicle_label}
                                          </td>
                                          <td className="py-2 px-3 whitespace-nowrap">
                                            {formatControlCentreDateTime(row.start) || '—'}
                                          </td>
                                          <td className="py-2 px-3 whitespace-nowrap">
                                            {row.open_ended
                                              ? <span className="text-warning">Ongoing</span>
                                              : (formatControlCentreDateTime(row.end) || '—')}
                                          </td>
                                          <td className="py-2 px-3 text-right whitespace-nowrap">
                                            {row.duration_hours.toFixed(1)}h
                                            {row.open_ended ? (
                                              <span className="text-muted-foreground text-xs ml-1">(so far)</span>
                                            ) : null}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
                });
              })()}
            </tbody>
          </table>
          
          {/* Pagination Controls */}
          {(() => {
            const driverDetails: DriverDetail[] = displayData?.driver_details || [];
            const filtered = driverDetails.filter(driver =>
              (
                driver.driver_name.toLowerCase().includes(searchQuery.toLowerCase())
                || (driver.emp_id && driver.emp_id.toLowerCase().includes(searchQuery.toLowerCase()))
              )
              && rowMatchesDriverTableFilter(driver, driverTableFilter),
            );
            
            if (filtered.length === 0) return null;
            
            const totalPages = Math.ceil(filtered.length / rowsPerPage);
            const startIndex = (currentPage - 1) * rowsPerPage;
            const endIndex = startIndex + rowsPerPage;
            
            return (
              <div className="flex items-center justify-between px-4 py-4 border-t border-border/50">
                {/* Left side: Results count and rows per page */}
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span>
                    Showing {startIndex + 1}-{Math.min(endIndex, filtered.length)} of {filtered.length} results
                  </span>
                  <span className="text-muted-foreground">•</span>
                  <div className="flex items-center gap-1">
                    <span className="text-xs">Show</span>
                    <select
                      className="bg-secondary/80 border border-border rounded px-2 py-1 text-xs text-white focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
                      value={rowsPerPage}
                      onChange={(e) => {
                        setRowsPerPage(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                    >
                      {[10, 25, 50, 100].map(n => (
                        <option key={n} value={n}>{n}</option>
                      ))}
                    </select>
                    <span className="text-xs">per page</span>
                  </div>
                </div>
                {/* Right side: Page navigation */}
                {totalPages > 1 && (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(currentPage - 1)}
                      title="Previous page"
                      className="hover:bg-secondary/50"
                    >
                      <ChevronDown className="w-4 h-4 rotate-90" />
                    </Button>
                    <span className="text-sm text-foreground min-w-20 text-center">
                      Page {currentPage}/{totalPages}
                    </span>
                    <input
                      type="number"
                      min={1}
                      max={totalPages}
                      value={currentPage}
                      onChange={(e) => {
                        let val = Number(e.target.value);
                        if (val < 1) val = 1;
                        if (val > totalPages) val = totalPages;
                        setCurrentPage(val);
                      }}
                      className="w-12 text-center bg-secondary/80 border border-border rounded px-2 py-1 text-sm text-white focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage(currentPage + 1)}
                      title="Next page"
                      className="hover:bg-secondary/50"
                    >
                      <ChevronDown className="w-4 h-4 -rotate-90" />
                    </Button>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
        </div>
      </div>
      </>)}

      {/* Violations Modal */}
      {showViolationsModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowViolationsModal(false)}>
          <div className="bg-popover rounded-lg border border-border shadow-2xl max-w-4xl w-full max-h-screen-90 overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div>
                <h3 className="text-2xl font-bold text-white flex items-center gap-2">
                  <FileWarning className="w-6 h-6 text-destructive" />
                  All Violations ({displayData?.infringement_count || 0})
                </h3>
                <p className="text-sm text-muted-foreground mt-1">Violations for the selected time period</p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowViolationsModal(false)}
                className="rounded-lg hover:bg-card"
              >
                <X className="w-5 h-5 text-muted-foreground" />
              </Button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6">
              {displayData?.driver_details && displayData.driver_details.length > 0 ? (
                (() => {
                  // Collect all violations from all drivers
                  const allViolations: Array<{
                    driver_id: string;
                    driver_name: string;
                    emp_id?: string | null;
                    violation: DriverDetail['violations'][0];
                  }> = [];
                  
                  displayData.driver_details.forEach(driver => {
                    (driver.violations || []).forEach(violation => {
                      allViolations.push({
                        driver_id: driver.driver_id,
                        driver_name: driver.driver_name,
                        emp_id: driver.emp_id,
                        violation
                      });
                    });
                  });
                  
                  if (allViolations.length === 0) {
                    return (
                      <div className="text-center py-12 text-muted-foreground">
                        <CheckCircle className="w-16 h-16 mx-auto mb-4 text-success opacity-50" />
                        <p className="text-lg">No violations found for the selected time period</p>
                        <p className="text-sm mt-2">All drivers are compliant!</p>
                      </div>
                    );
                  }
                  
                  // Sort by date (newest first)
                  allViolations.sort((a, b) => {
                    const dateA = new Date(a.violation.date);
                    const dateB = new Date(b.violation.date);
                    return dateB.getTime() - dateA.getTime();
                  });
                  
                  return (
                    <div className="space-y-3">
                      {allViolations.map((item, idx) => {
                        const severityColor = item.violation.severity === 'critical' ? 'bg-destructive/20 border-destructive/50 text-destructive' :
                                             item.violation.severity === 'warning' ? 'bg-warning/20 border-warning/50 text-warning' :
                                             'bg-ring/20 border-ring/50 text-ring';
                        const severityBadge = item.violation.severity === 'critical' ? 'Critical' :
                                             item.violation.severity === 'warning' ? 'Warning' :
                                             'Minor';
                        
                        return (
                          <div key={idx} className={`p-4 rounded-lg border ${severityColor} hover:opacity-90 transition-opacity`}>
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                  <div className="font-semibold text-white">{item.violation.label || item.violation.description || item.violation.type}</div>
                                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                    item.violation.severity === 'critical' ? 'bg-destructive/30 text-destructive' :
                                    item.violation.severity === 'warning' ? 'bg-warning/30 text-warning' :
                                    'bg-ring/30 text-ring'
                                  }`}>
                                    {severityBadge}
                                  </span>
                                </div>
                                <p className="text-sm opacity-90 mb-2">{item.violation.description}</p>
                                <div className="flex items-center gap-4 text-xs opacity-75">
                                  <div className="flex items-center gap-1">
                                    <Users className="w-3 h-3" />
                                    <span>{item.driver_name}{item.emp_id ? ` (Emp ${item.emp_id})` : ''}</span>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <span>Date: {item.violation.date}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <FileWarning className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p>No driver data available</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* KPI Detail Modal */}
      <KPIDataModal
        isOpen={kpiModal.open}
        onClose={() => {
          setKpiModal({ open: false, title: '' });
          onHighlightConsumed?.();
        }}
        title={kpiModal.title}
        subtitle={kpiModal.subtitle}
        icon={Users}
        data={driverRows}
        columns={kpiModalColumns}
        filterFn={kpiModal.filterFn}
        defaultSort={kpiModal.defaultSort}
        searchKeys={['driver_name', 'emp_id', 'compliance_status', 'last_activity']}
        // Drivers with violations expand to show what they were (date, rule, severity).
        renderExpandedRow={(row) => ((row.violations?.length ?? 0) > 0 ? <DriverViolationList row={row} reviews={violationReviews} /> : null)}
        rowKey={(row) => String(row.driver_id ?? row.driver_name)}
        exportFilename={`drivers_${new Date().toISOString().split('T')[0]}.csv`}
      />
    </div>
  );
}
